import AgentDraftGate from "./AgentDraftGate";
import { bindAgentDraftPayment, resolveAgentDraftPaymentOperation } from "utils/agentDraft";
import { hasMissingReturnAddressFields } from "utils/returnAddress";
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Stack,
  Button,
  Drawer,
  Grid,
  TextField,
} from "@mui/material";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import { PickupAddress } from "components/v2/Common/Address";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  StyledExchange,
  StyledPaymentDiv,
  StyledReturnCart,
} from "components/v2/ReturnCart/styled";
import BottomActionBar from "components/v2/Common/BottomActionBar";
import { getMerchantConfig, getRmsConfig } from "features/config/configSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  getOrders,
  getSelectedOrder,
  getUUID,
  updateSelectedOrder,
  updateUUID,
} from "features/orders/orderSlice";
import {
  addPaymentDetails,
  addPaymentStatus,
  addReturnedProducts,
  addRefundJSON,
  addRefundSource,
  getRefundJSON,
  getRefundSource,
  getReturnedProducts,
} from "features/returnedProducts/returnedProductSlice";
import { ReactComponent as EditIcon } from "assets/edit.svg";
import cartImage from "assets/empty_cart.svg";
import { PriceBox } from "components/v2/Common/PriceBox";
import CheckmarkControl from "components/v2/Common/CheckmarkControl";
import ResolutionNotes from "components/v2/Common/ResolutionNotes";
import {
  createRequest,
  generatePayment,
  paymentStatus,
  paymentValidate,
  validateReturnCartAvailability,
} from "utils/api";
import {
  addAddress,
  addShipmentMethod,
  getAddress,
  getShipmentMethod,
} from "features/address/addresSlice";
import {
  addUploadMediaIds,
  getPreviews,
  getUploadMediaIds,
} from "features/product/productSlice";
import { ClipLoader } from "react-spinners";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getEmail, getFirstName, getLastName } from "features/user/userSlice";
import { LazyLoadImage } from "react-lazy-load-image-component";
import LineItemCard from "components/v2/Common/LineItemCard";
import { getEncipherencode } from "utils/encipherencode";
import { getWarrantyClaimParam } from "utils/warrantyClaim";
import {
  clearReturnCartContext,
  getReturnCartContext,
  mergeReturnCartContext,
  saveReturnCartContext,
} from "utils/returnCartSession";
import { stripVersionPrefix, versionedPath } from "utils/routes";
import { V2PaymentDetailsBoneyardLoader } from "components/v2/Loaders/BoneyardLoaders";
import {
  getEmailValidationError,
  getPhoneValidationError,
} from "utils/contactValidation";
import PageHeader from "components/v2/Common/PageHeader";
import PageIntro from "components/v2/Common/PageIntro";
import { getApiErrorMessage } from "utils/apiError";
import { hasReturnMediaUploads } from "utils/returnMedia";
import { isReasonFirstQuoteMethodAvailable } from "utils/reasonFirstRefund";
import {
  createSubmissionLock,
  getCreateConflict,
  getIykaQuoteConflict,
  hasActionableAvailabilityConflicts,
} from "utils/returnRequestAvailability";
import {
  deriveRequestReturnType,
  hasExchangeFulfillmentRequestType,
  hasRefundRequestType,
  isExchangeOnlyRequestType,
} from "utils/returnTypes";

const ADDRESS_INVALID_CHAR_PATTERN = /[!@#$%^&*)(+=<>{}[\];|~`_?]/;
const PAYMENT_DRAWER_INITIAL_STATE = {
  open: false,
  status: "idle",
  message: "",
  requestId: "",
  referenceId: "",
  transactionId: "",
  orderId: "",
};

const formatPaymentAmount = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatCurrencyAmount = (value) =>
  new Intl.NumberFormat("en-IN", {
    currency: "INR",
    maximumFractionDigits: 2,
    minimumFractionDigits: Number(value || 0) % 1 === 0 ? 0 : 2,
    style: "currency",
  }).format(Number(value || 0));

const WARRANTY_REPAIR_FLOW_TYPES = new Set([
  "damage_repair",
  "general_service",
]);

const getWarrantyPaymentTotal = (details = {}) => {
  const safeDetails = details || {};
  return Number(
    safeDetails.amount_to_pay ??
      safeDetails.total_warranty_charges ??
      safeDetails.warranty_charges ??
      0,
  );
};

const getWarrantyChargeSnapshots = (item) =>
  item?.additional_charge_snapshots || item?.warranty_additional_charge_snapshots || [];

const isWarrantyRepairFlow = (products = [], details = {}) => {
  const safeDetails = details || {};
  return products.some((product) =>
    WARRANTY_REPAIR_FLOW_TYPES.has(product?.warranty_claim_flow_type),
  ) ||
  (safeDetails.line_items || []).some((item) =>
    WARRANTY_REPAIR_FLOW_TYPES.has(item?.warranty_claim_flow_type),
  );
};

const getWarrantyLineItem = (product, details = {}, products = []) => {
  const quoteItems = details?.line_items || [];
  // Quote rows preserve input order, including separate selections of one source line.
  const index = products.indexOf(product);
  if (index >= 0) return quoteItems[index] || product;
  const matches = quoteItems.filter((item) =>
    idsMatch(item?.line_item_id, product?.line_item_id),
  );
  return matches.length === 1 ? matches[0] : product;
};

const getWarrantyFlowLabel = (product, details = {}, products = []) => {
  const lineItem = getWarrantyLineItem(product, details, products);
  const flowType = lineItem?.warranty_claim_flow_type ?? product?.warranty_claim_flow_type;
  if (lineItem?.warranty_claim_flow_label) {
    return lineItem.warranty_claim_flow_label;
  }
  if (flowType === "general_service") {
    return "General Service";
  }
  if (flowType === "damage_repair") {
    return "Damage Repair";
  }
  return "Warranty Claim";
};

const getWarrantyAnswers = (product, details = {}, products = []) => {
  const lineItem = getWarrantyLineItem(product, details, products);
  const answers = [
    lineItem?.questionnaire_answers,
    lineItem?.warranty_questionnaire_answers,
    product?.questionnaire_answers,
    product?.warranty_questionnaire_answers,
  ].find((value) => Array.isArray(value) && value.length) || [];

  return Array.isArray(answers)
    ? answers.filter(
        (answer) =>
          answer?.question_label &&
          [answer.answer_label, answer.answer_text, answer.answer_value]
            .some((value) => value !== undefined && value !== null && value !== ""),
      )
    : [];
};

const flattenWarrantyComponents = (product, details = {}, products = []) => {
  const lineItem = getWarrantyLineItem(product, details, products);
  const snapshots = [];
  const chargeSnapshots = getWarrantyChargeSnapshots(lineItem);

  chargeSnapshots.forEach((charge) => {
    if (charge?.component_snapshot &&
      (!charge.charge_category || charge.charge_category === "component")) {
      snapshots.push({
        ...charge.component_snapshot,
        charge_amount: charge.amount ?? charge.charge_amount ?? charge.component_snapshot.charge_amount,
        label: charge.label,
      });
    }
  });

  [
    ...getWarrantyAnswers(product, details, products),
    ...(product?.questionnaire_answers || []),
    ...(product?.warranty_questionnaire_answers || []),
  ].forEach((answer) => {
    const answerSnapshots = Array.isArray(answer.component_snapshot)
      ? answer.component_snapshot
      : answer.component_snapshot ? [answer.component_snapshot] : [];
    snapshots.push(...answerSnapshots);
  });

  const seen = new Set();
  return snapshots
    .map((component) => ({
      amount: component.charge_amount ?? 0,
      covered: component.coverage_applied === true,
      waived: component.charge_waived === true,
      image: component.image_url || component.component_image_url || "",
      key: component.component_variant_id != null
          ? `variant:${component.component_variant_id}`
          : component.component_id != null
            ? `component:${component.component_id}`
            : component.component_sku || component.component_name || component.label,
      name:
        component.component_name ||
        component.label ||
        component.component_type_label ||
        "Repair component",
    }))
    .filter((component) => {
      const key = component.key || `${component.name}:${component.amount}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
};

const hasExchangeFulfillmentChargeDetails = (details = {}) =>
  [
    details.amount_to_pay,
    details.reimbursement,
    details.exchange_charges,
    details.preapplied_exchange_charge,
    details.shipping_charges,
    details.total_exchange_charges,
  ].some((value) => Number(value || 0) > 0);

const normalizeCreateAdditionalErrors = (error) => {
  const data = error?.response?.data || {};
  const detail = data?.detail || {};
  const rawAdditionalErrors =
    detail?.additional_err ||
    detail?.additonal_err ||
    data?.additional_err ||
    data?.additonal_err ||
    null;

  if (!rawAdditionalErrors) {
    if (data?.variant_id || data?.product_id || data?.exchange_variant_id) {
      return [data];
    }
    return [];
  }

  return Array.isArray(rawAdditionalErrors)
    ? rawAdditionalErrors.filter(Boolean)
    : [rawAdditionalErrors];
};

const idsMatch = (left, right) =>
  left !== undefined &&
  left !== null &&
  right !== undefined &&
  right !== null &&
  String(left) === String(right);

const createErrorMatchesProduct = (product, createError) => {
  const checks = [];

  if (createError?.product_id !== undefined && product?.product_id !== undefined) {
    checks.push(idsMatch(product.product_id, createError.product_id));
  }
  if (
    createError?.line_item_id !== undefined &&
    product?.line_item_id !== undefined
  ) {
    checks.push(idsMatch(product.line_item_id, createError.line_item_id));
  }
  if (createError?.variant_id !== undefined && product?.variant_id !== undefined) {
    checks.push(idsMatch(product.variant_id, createError.variant_id));
  }
  if (
    createError?.exchange_variant_id !== undefined &&
    product?.exchange_variant_id !== undefined
  ) {
    checks.push(
      idsMatch(product.exchange_variant_id, createError.exchange_variant_id),
    );
  }

  return checks.length > 0 && checks.every(Boolean);
};

function ReturnCart({ agentDraft }) {
  const location = useLocation();
  const currentPath = stripVersionPrefix(location.pathname);

  const [hitValidateApi, setHitValidateApi] = useState("validate");
  const refundSource = useSelector(getRefundSource);
  const merchantConfig = useSelector(getMerchantConfig);
  const rmsConfig = useSelector(getRmsConfig);
  const allOrders = useSelector(getOrders);
  const selectedOrder = useSelector(getSelectedOrder);
  const defaultShippingMethod = useSelector(getShipmentMethod);
  const addressDetails = useSelector(getAddress);
  const uploads = useSelector(getPreviews);
  const storedReturnedProducts = useSelector(getReturnedProducts);
  const returnedProducts = agentDraft ? agentDraft.items : storedReturnedProducts;
  const mergeAgentProducts = agentDraft?.mergeProducts;
  const withAgentProducts = useCallback(
    (items) => mergeAgentProducts ? mergeAgentProducts(items) : items,
    [mergeAgentProducts],
  );
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  const [requestCreated, setRequestCreated] = useState(false);
  const createSubmissionLockRef = useRef(null);
  if (!createSubmissionLockRef.current) {
    createSubmissionLockRef.current = createSubmissionLock();
  }
  const unique_id = useSelector(getUUID);
  const [createItemErrorDetails, setCreateItemErrorDetails] = useState([]);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [validatedQuoteKey, setValidatedQuoteKey] = useState(null);
  const [quoteError, setQuoteError] = useState("");
  const [quoteAttempt, setQuoteAttempt] = useState(0);
  const currentQuoteKeyRef = useRef(null);
  const paymentDetailsRef = useRef(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [paymentMethodError, setPaymentMethodError] = useState(false);
  const [refundModeError, setRefundModeError] = useState(false);
  const [calculatingLoader, setCalculatingLoader] = useState(false);
  const [initialLoader, setInitialLoader] = useState(true);
  const [shipmentMethod, setShipmentMethod] = useState(defaultShippingMethod);
  const [requestConsentAccepted, setRequestConsentAccepted] = useState(false);
  const [requestConsentAttempted, setRequestConsentAttempted] = useState(false);
  const [warrantyComponentsOpen, setWarrantyComponentsOpen] = useState(true);
  const [paymentDrawer, setPaymentDrawer] = useState(
    PAYMENT_DRAWER_INITIAL_STATE,
  );

  const missingFirstName = useSelector(getFirstName);
  const missingLastName = useSelector(getLastName);
  const missingEmail = useSelector(getEmail);
  const uploadedMediaIds = useSelector(getUploadMediaIds);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const encipherencode = getEncipherencode(params);
  const warrantyClaimParam = getWarrantyClaimParam(params);
  const isWarrantyClaim =
    warrantyClaimParam?.toLowerCase() === "true";

  const reference_id = params.get("reference_id");
  const paymentTransactionId = params.get("txn_id");
  const paymentOrderId = params.get("order_id");
  const isPaymentReturn = Boolean(
    paymentOrderId && (reference_id || paymentTransactionId),
  );
  const rdv = params.get("rdv");

  const upload_media =
    hasReturnMediaUploads(uploadedMediaIds) ||
    (Array.isArray(uploads) && uploads.length > 0);

  const [updatedReturnedProducts, setUpdatedReturnedProducts] =
    useState(returnedProducts);
  const visibleShippingMethods = useMemo(
    () => [
      ...(rmsConfig.show_shipping_preference && rmsConfig.supports_return_pickup
        ? ["merchant_handles_return_shipment"]
        : []),
      ...(rmsConfig.show_shipping_preference &&
        !rmsConfig.supports_return_pickup &&
        rmsConfig.self_ship
        ? ["customer_ships_back"]
        : []),
    ],
    [
      rmsConfig.self_ship,
      rmsConfig.show_shipping_preference,
      rmsConfig.supports_return_pickup,
    ],
  );

  const dispatch = useDispatch();
  const syncShipmentMethod = (method) => {
    setShipmentMethod(method);
    dispatch(addShipmentMethod(method));
  };

  const return_type = deriveRequestReturnType(
    updatedReturnedProducts.map((item) => item.return_type),
    reference_id && reference_id !== null && reference_id.includes("REXC")
      ? "refund_exchange"
      : "exchange",
  );
  const reasonFirstRefundItems = updatedReturnedProducts.filter(
    (item) => item.return_type === "refund" && item.reason_mode === "reason_first",
  );
  const legacyRefundItems = updatedReturnedProducts.filter(
    (item) => item.return_type === "refund" && item.reason_mode !== "reason_first",
  );
  const hasReasonFirstRefundItems = reasonFirstRefundItems.length > 0;
  const hasLegacyRefundItems = legacyRefundItems.length > 0;
  const reasonFirstRefundMethodUnavailableItems = reasonFirstRefundItems.filter((item) => {
    const itemIndex = updatedReturnedProducts.findIndex((candidate) =>
      String(candidate.line_item_id) === String(item.line_item_id),
    );
    const quoteItem = paymentDetails?.line_items?.find((lineItem) =>
      String(lineItem?.line_item_id) === String(item.line_item_id),
    ) || paymentDetails?.line_items?.[itemIndex];
    return Boolean(item.customer_preferred_refund_method) &&
      !isReasonFirstQuoteMethodAvailable(quoteItem, item.customer_preferred_refund_method);
  });
  const updateReasonFirstRefundMethod = (nextProducts) => {
    setUpdatedReturnedProducts(nextProducts);
    dispatch(addReturnedProducts(withAgentProducts(nextProducts)));
  };
  const isReplacementRequest = return_type === "replacement";
  const refund_json = useSelector(getRefundJSON);
  const [storedContext, setStoredContext] = useState(null);
  const [isRestoringState, setIsRestoringState] = useState(true);
  const [didCheckEntryState, setDidCheckEntryState] = useState(false);
  const refundBankDetails = hasReasonFirstRefundItems
    ? refund_json?.bank_details || {}
    : refund_json?.refund_method === "bank_transfer"
      ? refund_json.bank_details || {}
      : {};
  const accountNumber = refundBankDetails.bank_account_number || null;
  const confirmAccNumber =
    refundBankDetails.confirm_bank_account_number || null;
  const accHolderName = refundBankDetails.bank_account_holder_name || null;
  const ifsc = refundBankDetails.bank_ifsc_code || null;
  const upiValue = hasReasonFirstRefundItems
    ? refund_json?.upi?.upi_code || ""
    : refund_json?.refund_method === "upi" && refund_json.upi?.upi_code
      ? refund_json.upi.upi_code
      : "";

  const hasRequiredOrderFields = (order) =>
    Boolean(
      order &&
      order.order_number &&
      order.order_id !== null &&
      order.order_id !== undefined &&
      typeof order.is_cod === "boolean" &&
      order.total_order_price !== null &&
      order.total_order_price !== undefined,
    );

  const hasAddressState = (details) =>
    Boolean(details && Object.values(details).some((value) => Boolean(value)));

  const getPreferredValue = (...values) =>
    values.find(
      (value) => value !== undefined && value !== null && value !== "",
    );

  const matchedOrder = useMemo(
    () =>
      Object.values(allOrders).find(
        (order) => order.order_number === selectedOrder?.order_number,
      ) || null,
    [allOrders, selectedOrder],
  );

  const resolvedOrder = useMemo(() => {
    if (hasRequiredOrderFields(selectedOrder)) {
      return selectedOrder;
    }

    if (hasRequiredOrderFields(matchedOrder)) {
      return matchedOrder;
    }

    if (hasRequiredOrderFields(storedContext?.selectedOrder)) {
      return storedContext.selectedOrder;
    }

    return matchedOrder || selectedOrder || storedContext?.selectedOrder || {};
  }, [matchedOrder, selectedOrder, storedContext]);

  const selectedItem =
    resolvedOrder && Object.keys(resolvedOrder).length > 0
      ? [resolvedOrder]
      : [];

  const hasResolvedOrderContext = hasRequiredOrderFields(resolvedOrder);
  const hasReturnCart = updatedReturnedProducts.length > 0;
  const isReadyForValidation = Boolean(
    merchantConfig?.merchant_id && hasResolvedOrderContext && hasReturnCart,
  );
  // Use the same inputs as paymentValidate, including answers and quantities.
  const quoteKey = JSON.stringify([
    merchantConfig.merchant_id, resolvedOrder.order_number,
    resolvedOrder.order_id, resolvedOrder.is_cod, resolvedOrder.total_order_price,
    return_type, shipmentMethod, updatedReturnedProducts, refundSource,
    resolvedOrder.preapplied_exchange_charge, resolvedOrder.preapplied_refund_charge,
    isWarrantyClaim,
  ]);
  currentQuoteKeyRef.current = quoteKey;
  const hasCurrentQuote = paymentDetails !== null && validatedQuoteKey === quoteKey;

  const [firstNameCheck, setFirstNameCheck] = useState(
    getPreferredValue(
      addressDetails.firstName,
      missingFirstName,
      resolvedOrder?.customer_first_name,
    ) || "",
  );
  const [lastNameCheck, setLastNameCheck] = useState(
    getPreferredValue(
      addressDetails.lastName,
      missingLastName,
      resolvedOrder?.customer_last_name,
    ) || "",
  );
  const [userPhoneNumber, setUserPhoneNumber] = useState(
    getPreferredValue(
      storedContext?.customerContact?.userPhoneNumber,
      resolvedOrder?.user_phone_number,
    ) || "",
  );
  const [userEmail, setUserEmail] = useState(
    getPreferredValue(
      storedContext?.customerContact?.userEmail,
      missingEmail,
      resolvedOrder?.user_email,
    ) || "",
  );
  const [wrongAddress, setWrongAddress] = useState(false);
  const [userInfoError, setUserInfoError] = useState(false);
  const [addressChange, setAddressChange] = useState(false);
  const [addressEditorOpen, setAddressEditorOpen] = useState(false);
  const [addressEditorError, setAddressEditorError] = useState("");
  const [addressFieldErrors, setAddressFieldErrors] = useState({});
  const [editAddressLine1, setEditAddressLine1] = useState(
    addressDetails.addressLine1 || "",
  );
  const [editAddressLine2, setEditAddressLine2] = useState(
    addressDetails.addressLine2 || "",
  );
  const [editCity, setEditCity] = useState(addressDetails.city || "");
  const [editState, setEditState] = useState(addressDetails.state || "");
  const [editPincode, setEditPincode] = useState(addressDetails.pincode || "");

  // Restore the flow once on entry; rerunning would overwrite active edits.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const context = getReturnCartContext();

    if (context) {
      setStoredContext(context);

      if (
        !hasRequiredOrderFields(selectedOrder) &&
        hasRequiredOrderFields(context.selectedOrder)
      ) {
        dispatch(updateSelectedOrder(context.selectedOrder));
      }

      if (
        (!returnedProducts || returnedProducts.length === 0) &&
        Array.isArray(context.returnedProducts) &&
        context.returnedProducts.length > 0
      ) {
        dispatch(addReturnedProducts(withAgentProducts(context.returnedProducts)));
      }

      if (
        (!defaultShippingMethod || defaultShippingMethod === "") &&
        context.shipmentMethod
      ) {
        dispatch(addShipmentMethod(context.shipmentMethod));
        setShipmentMethod(context.shipmentMethod);
      }

      if (context.refundSource !== undefined) {
        dispatch(addRefundSource(context.refundSource));
      }

      if (
        context.refundJSON &&
        Object.keys(context.refundJSON).length > 0
      ) {
        dispatch(addRefundJSON(context.refundJSON));
      }

      if (hasAddressState(context.addressDetails)) {
        dispatch(addAddress(context.addressDetails));
      }

      if (
        (!uploadedMediaIds || uploadedMediaIds.length === 0) &&
        Array.isArray(context.uploadMediaIds) &&
        context.uploadMediaIds.length > 0
      ) {
        dispatch(addUploadMediaIds(context.uploadMediaIds));
      }

      if (!unique_id && context.uniqueId) {
        dispatch(updateUUID(context.uniqueId));
      }
    }

    setIsRestoringState(false);
  }, []);

  useEffect(() => {
    if (defaultShippingMethod && defaultShippingMethod !== shipmentMethod) {
      setShipmentMethod(defaultShippingMethod);
    }
  }, [defaultShippingMethod, shipmentMethod]);

  useEffect(() => {
    paymentDetailsRef.current = paymentDetails;
  }, [paymentDetails]);

  useEffect(() => {
    if (
      isRestoringState ||
      !rmsConfig.show_shipping_preference ||
      visibleShippingMethods.length === 0 ||
      visibleShippingMethods.includes(shipmentMethod)
    ) {
      return;
    }

    syncShipmentMethod(visibleShippingMethods[0]);
  }, [
    isRestoringState,
    rmsConfig.show_shipping_preference,
    shipmentMethod,
    visibleShippingMethods,
  ]);

  useEffect(() => {
    if (addressChange) {
      setAddressEditorOpen(true);
    }
  }, [addressChange]);

  useEffect(() => {
    setEditAddressLine1(addressDetails.addressLine1 || "");
    setEditAddressLine2(addressDetails.addressLine2 || "");
    setEditCity(addressDetails.city || "");
    setEditState(addressDetails.state || "");
    setEditPincode(addressDetails.pincode || "");
  }, [addressDetails]);
  const emailValidationError = getEmailValidationError(userEmail);
  const phoneValidationError = getPhoneValidationError(userPhoneNumber);
  const hasInvalidContact = Boolean(
    emailValidationError || phoneValidationError,
  );


  const validateInlineAddressFields = () => {
    const pin = String(editPincode || "").trim();
    const line1 = String(editAddressLine1 || "").trim();
    const line2 = String(editAddressLine2 || "").trim();
    const city = String(editCity || "").trim();
    const state = String(editState || "").trim();
    const firstName = String(firstNameCheck || "").trim();
    const lastName = String(lastNameCheck || "").trim();
    const email = String(userEmail || "").trim();
    const phone = String(userPhoneNumber || "").trim();
    const errors = {};

    if (!firstName) {
      errors.firstName = "*required";
    }
    if (!lastName) {
      errors.lastName = "*required";
    }
    if (!email) {
      errors.email = "*required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = "Enter a valid email";
    }
    if (!phone) {
      errors.phone = "*required";
    } else if (phoneValidationError) {
      errors.phone = phoneValidationError;
    }
    if (!line1) {
      errors.addressLine1 = "*required";
    } else if (line1.length < 15) {
      errors.addressLine1 = "Address Line 1 must be at least 15 characters";
    } else if (ADDRESS_INVALID_CHAR_PATTERN.test(line1)) {
      errors.addressLine1 = "Remove special characters";
    }
    if (line2 && (line2.length < 10 || line2.length > 70)) {
      errors.addressLine2 = "Address Line 2 must be 10-70 characters";
    } else if (line2 && ADDRESS_INVALID_CHAR_PATTERN.test(line2)) {
      errors.addressLine2 = "Remove special characters";
    }
    if (!city) {
      errors.city = "*required";
    } else if (ADDRESS_INVALID_CHAR_PATTERN.test(city)) {
      errors.city = "Remove special characters";
    }
    if (!state) {
      errors.state = "*required";
    } else if (ADDRESS_INVALID_CHAR_PATTERN.test(state)) {
      errors.state = "Remove special characters";
    }
    if (!pin) {
      errors.pincode = "*required";
    } else if (pin.length !== 6 || ADDRESS_INVALID_CHAR_PATTERN.test(pin)) {
      errors.pincode = "Enter a valid 6-digit PIN code";
    }
    if (line1.length + line2.length > 150) {
      errors.addressLine1 = "Address is too long";
      if (line2) {
        errors.addressLine2 = "Address is too long";
      }
    }

    return errors;
  };

  const clearAddressFieldError = (fieldName) => {
    setAddressFieldErrors((currentErrors) => {
      if (!currentErrors[fieldName]) {
        return currentErrors;
      }

      const nextErrors = { ...currentErrors };
      delete nextErrors[fieldName];
      return nextErrors;
    });
  };

  const handleSaveInlineAddress = () => {
    const fieldErrors = validateInlineAddressFields();
    if (Object.keys(fieldErrors).length > 0) {
      setAddressFieldErrors(fieldErrors);
      setAddressEditorError("Please fix the highlighted fields.");
      return;
    }
    const pin = String(editPincode || "").trim();
    setAddressEditorError("");
    setAddressFieldErrors({});
    const nextAddressDetails = {
      ...addressDetails,
      firstName: String(firstNameCheck || "").trim(),
      lastName: String(lastNameCheck || "").trim(),
      addressLine1: editAddressLine1.trim(),
      addressLine2: (editAddressLine2 || "").trim(),
      city: editCity.trim(),
      state: editState.trim(),
      pincode: pin,
    };
    dispatch(addAddress(nextAddressDetails));
    mergeReturnCartContext({
      addressDetails: nextAddressDetails,
      customerContact: {
        firstNameCheck: nextAddressDetails.firstName,
        lastNameCheck: nextAddressDetails.lastName,
        userPhoneNumber,
        userEmail,
      },
    });
    setAddressChange(false);
    setWrongAddress(false);
    setUserInfoError(false);
    setAddressEditorOpen(false);
  };

  useEffect(() => {
    setFirstNameCheck(
      (previousValue) =>
        getPreferredValue(
          previousValue,
          storedContext?.customerContact?.firstNameCheck,
          addressDetails.firstName,
          missingFirstName,
          resolvedOrder?.customer_first_name,
        ) || "",
    );
    setLastNameCheck(
      (previousValue) =>
        getPreferredValue(
          previousValue,
          storedContext?.customerContact?.lastNameCheck,
          addressDetails.lastName,
          missingLastName,
          resolvedOrder?.customer_last_name,
        ) || "",
    );
    setUserPhoneNumber(
      (previousValue) =>
        getPreferredValue(
          previousValue,
          storedContext?.customerContact?.userPhoneNumber,
          resolvedOrder?.user_phone_number,
        ) || "",
    );
    setUserEmail(
      (previousValue) =>
        getPreferredValue(
          previousValue,
          storedContext?.customerContact?.userEmail,
          missingEmail,
          resolvedOrder?.user_email,
        ) || "",
    );
  }, [
    addressDetails,
    missingEmail,
    missingFirstName,
    missingLastName,
    resolvedOrder,
    storedContext,
  ]);

  useEffect(() => {
    if (isRestoringState || didCheckEntryState) {
      return;
    }

    if (isPaymentReturn) {
      setDidCheckEntryState(true);
      return;
    }

    const hasStoredOrderContext = hasRequiredOrderFields(
      storedContext?.selectedOrder,
    );
    const hasStoredCart =
      Array.isArray(storedContext?.returnedProducts) &&
      storedContext.returnedProducts.length > 0;

    if (!hasResolvedOrderContext) {
      const message =
        "Your session expired or this page was reloaded. Please review your return selection again.";
      toast.error(message, {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
      });
      clearReturnCartContext();
      setDidCheckEntryState(true);
      let url = versionedPath("/orders", location.pathname);
      if (encipherencode) {
        url += `?encipherencode=${encipherencode}`;
      }
      navigate(url);
      return;
    }

    if (!hasReturnCart && (!returnedProducts.length || !hasStoredCart)) {
      toast.error(
        "Your session expired or this page was reloaded. Please review your return selection again.",
        {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 6000,
          hideProgressBar: false,
          closeOnClick: true,
        },
      );
      clearReturnCartContext();
      setDidCheckEntryState(true);
      let url =
        hasStoredOrderContext || hasResolvedOrderContext
          ? versionedPath("/products", location.pathname)
          : versionedPath("/orders", location.pathname);
      if (encipherencode) {
        url += `?encipherencode=${encipherencode}`;
      }
      navigate(url);
      return;
    }

    setDidCheckEntryState(true);
  }, [
    didCheckEntryState,
    encipherencode,
    hasResolvedOrderContext,
    hasReturnCart,
    isPaymentReturn,
    isRestoringState,
    navigate,
    returnedProducts.length,
    storedContext,
  ]);

  useEffect(() => {
    if (isRestoringState || !hasResolvedOrderContext) {
      return;
    }

    saveReturnCartContext({
      selectedOrder: resolvedOrder,
      returnedProducts: withAgentProducts(updatedReturnedProducts),
      shipmentMethod,
      refundSource,
      refundJSON: refund_json,
      addressDetails,
      uploadMediaIds: uploadedMediaIds,
      uniqueId: unique_id,
      customerContact: {
        firstNameCheck,
        lastNameCheck,
        userPhoneNumber,
        userEmail,
      },
    });
  }, [
    addressDetails,
    firstNameCheck,
    isRestoringState,
    lastNameCheck,
    refundSource,
    refund_json,
    resolvedOrder,
    shipmentMethod,
    unique_id,
    updatedReturnedProducts,
    uploadedMediaIds,
    userEmail,
    userPhoneNumber,
    hasResolvedOrderContext,
    withAgentProducts,
  ]);

  useEffect(() => {
    if (!isPaymentReturn || !merchantConfig?.merchant_id) {
      return undefined;
    }

    let cancelled = false;
    let intervalId;

    setPaymentDrawer((prev) => ({
      ...prev,
      open: true,
      status: "verifying",
      message: "Verifying payment status...",
      referenceId: reference_id || prev.referenceId,
      transactionId: paymentTransactionId || prev.transactionId,
      orderId: paymentOrderId || prev.orderId,
    }));

    const checkPaymentStatus = () => {
      paymentStatus(
        merchantConfig.merchant_id,
        paymentTransactionId,
        reference_id,
        paymentOrderId,
        false,
        true,
      )
        .then((resp) => {
          if (cancelled) {
            return;
          }

          if (resp.data?.access_token) {
            sessionStorage.setItem("access_token", resp.data.access_token);
            localStorage.setItem("access_token", resp.data.access_token);
          }

          const paymentData = normalizePaymentResponse(resp.data);
          const paymentStatusValue = String(
            paymentData.payment_status || "",
          ).toLowerCase();
          const paymentSucceeded =
            paymentData.payment_done === true ||
            ["completed", "success", "paid"].includes(paymentStatusValue);
          const normalRequestCreated = Boolean(resp.data?.return_request_id || resp.data?.request_id || resp.data?.return_request?.id);
          const paymentReceivedWithoutRequest = Boolean(paymentSucceeded &&
            (paymentData.success === false || !normalRequestCreated));
          const paymentFailed = paymentReceivedWithoutRequest ||
            (paymentStatusValue === "failed" && !paymentData.success);

          const completedOperationId = agentDraft && paymentSucceeded && normalRequestCreated && !paymentReceivedWithoutRequest
            ? resolveAgentDraftPaymentOperation(merchantConfig.merchant_id, resolvedOrder.order_id, {
              reference_id: reference_id || paymentData.reference_id,
              transaction_id: paymentTransactionId || paymentData.transaction_id,
              order_id: paymentOrderId || paymentData.order_id,
            })
            : null;
          const confirmedPaymentData = completedOperationId
            ? { ...paymentData, completed_operation_id: completedOperationId }
            : paymentData;
          dispatch(addPaymentStatus(confirmedPaymentData));
          dispatch(addPaymentDetails(confirmedPaymentData));

          if (paymentFailed) {
            clearInterval(intervalId);
            setPaymentDrawer((prev) => ({
              ...prev,
              open: true,
              status: "failed",
              message: paymentReceivedWithoutRequest
                ? agentDraft
                  ? "Payment received, but your normal request needs assistance. Your agent draft is saved. Please contact the store with these references. Do not pay again."
                  : "Payment received, but your request needs assistance. Please contact the store with your payment reference. Do not pay again."
                : getPaymentFailureMessage(paymentData),
              paymentReceived: paymentReceivedWithoutRequest,
              requestId: paymentData.return_request_id || prev.requestId,
              referenceId: paymentData.reference_id || prev.referenceId,
              transactionId: paymentData.transaction_id || prev.transactionId,
              orderId: paymentData.order_id || prev.orderId,
            }));
            return;
          }

          if (paymentSucceeded && normalRequestCreated) {
            clearInterval(intervalId);
            setPaymentDrawer((prev) => ({
              ...prev,
              open: true,
              status: "success",
              message: "Payment successful. Redirecting...",
              requestId: paymentData.return_request_id || prev.requestId,
              referenceId: paymentData.reference_id || prev.referenceId,
              transactionId: paymentData.transaction_id || prev.transactionId,
              orderId: paymentData.order_id || prev.orderId,
            }));
            setTimeout(() => {
              if (!cancelled) {
                navigateToSuccess(paymentData);
              }
            }, 1200);
            return;
          }

          setPaymentDrawer((prev) => ({
            ...prev,
            open: true,
            status: "verifying",
            message: "Verifying payment status...",
            requestId: paymentData.return_request_id || prev.requestId,
            referenceId: paymentData.reference_id || prev.referenceId,
            transactionId: paymentData.transaction_id || prev.transactionId,
            orderId: paymentData.order_id || prev.orderId,
          }));
        })
        .catch((err) => {
          if (cancelled) {
            return;
          }
          clearInterval(intervalId);
          setPaymentDrawer((prev) => ({
            ...prev,
            open: true,
            status: "failed",
            message: getApiErrorMessage(
              err,
              "Payment status could not be verified. Please retry.",
            ),
          }));
        });
    };

    checkPaymentStatus();
    intervalId = setInterval(checkPaymentStatus, 5000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [
    dispatch,
    isPaymentReturn,
    merchantConfig?.merchant_id,
    paymentOrderId,
    paymentTransactionId,
    reference_id,
    resolvedOrder.order_id,
  ]);

  useEffect(() => {
    const totalAddressLength =
      (addressDetails.addressLine1 || "").length +
      (addressDetails.addressLine2 && addressDetails.addressLine2 !== null
        ? addressDetails.addressLine2.length
        : 0);
    if (selectedItem.length > 0) {
      if (
        hasMissingReturnAddressFields(addressDetails) ||
        ADDRESS_INVALID_CHAR_PATTERN.test(addressDetails.pincode) ||
        ADDRESS_INVALID_CHAR_PATTERN.test(addressDetails.addressLine1) ||
        ADDRESS_INVALID_CHAR_PATTERN.test(addressDetails.addressLine2) ||
        ADDRESS_INVALID_CHAR_PATTERN.test(addressDetails.state) ||
        ADDRESS_INVALID_CHAR_PATTERN.test(addressDetails.city) ||
        (addressDetails.addressLine1 || "").length < 15 ||
        (addressDetails.addressLine2 &&
          addressDetails.addressLine2 !== null &&
          addressDetails.addressLine2 !== "" &&
          (addressDetails.addressLine2.length < 10 ||
            addressDetails.addressLine2.length > 70)) ||
        totalAddressLength > 150
      ) {
        setAddressChange(true);
        setWrongAddress(true);
        setAddressFieldErrors((prev) => ({
          ...prev,
          ...validateInlineAddressFields(),
        }));
      } else if (
        hasInvalidContact ||
        !(
          userEmail !== undefined &&
          userPhoneNumber !== undefined &&
          userEmail !== null &&
          userPhoneNumber !== null &&
          userEmail !== "" &&
          userPhoneNumber !== "" &&
          firstNameCheck &&
          lastNameCheck &&
          firstNameCheck !== null &&
          lastNameCheck !== null &&
          firstNameCheck !== "" &&
          lastNameCheck !== ""
        )
      ) {
        setAddressChange(true);
        setUserInfoError(true);
        setAddressFieldErrors((prev) => ({
          ...prev,
          ...validateInlineAddressFields(),
        }));
      }
    } else {
      setWrongAddress(false);
      setAddressChange(false);
      setUserInfoError(false);
      setAddressFieldErrors({});
    }
  }, [
    addressDetails,
    firstNameCheck,
    hasInvalidContact,
    lastNameCheck,
    resolvedOrder,
    selectedItem.length,
    userEmail,
    userPhoneNumber,
  ]);

  useEffect(() => {
    if (
      paymentDetails !== null &&
      paymentMethod !== "cod" &&
      paymentMethod !== "prepaid"
    ) {
      if (paymentDetails.cod && paymentDetails.prepaid) {
        if (window.screen.width < 800) {
          setPaymentMethod("prepaid");
        } else {
          setPaymentMethod("cod-prepaid");
        }
      } else if (paymentDetails.cod && !paymentDetails.prepaid) {
        setPaymentMethod("cod");
      } else if (paymentDetails.prepaid && !paymentDetails.cod) {
        setPaymentMethod("prepaid");
      } else {
        setPaymentMethod(null);
      }
      setPaymentMethodError(false);
    }
    if (paymentDetails !== null) {
      if (
        paymentDetails?.offer_refund_mode_on === "item_level" &&
        paymentDetails?.item_level_refund_mode_offer_amount > 0
      ) {
        if (refundSource === paymentDetails?.offer_refund_mode) {
          const offerAmount =
            paymentDetails?.item_level_refund_mode_offer_amount;
          const refundProducts = updatedReturnedProducts.map((a) => {
            if (a.return_type === "refund") {
              if (a.refund_mode_offer_amount === offerAmount) {
                return a;
              }

              return {
                ...a,
                refund_mode_offer_amount: offerAmount,
              };
            }
            return a;
          });

          const hasOfferChange = refundProducts.some(
            (item, index) => item !== updatedReturnedProducts[index],
          );

          if (hasOfferChange) {
            setUpdatedReturnedProducts(refundProducts);
            setHitValidateApi("dont_validate");
            setTimeout(() => {
              setHitValidateApi("validate");
            }, 200);
          }
        }
      }
    }
  }, [paymentDetails, refundSource, updatedReturnedProducts]);

  useEffect(() => {
    let obsolete = false;
    if (
      requestCreated ||
      isRestoringState ||
      rdv === "no_validate" ||
      hitValidateApi !== "validate"
    ) {
      setCalculatingLoader(false);
      if (!isRestoringState) {
        setInitialLoader(false);
      }
      return;
    }

    if (!isReadyForValidation) {
      setCalculatingLoader(false);
      setInitialLoader(false);
      return;
    }

    setCalculatingLoader(true);
    setInitialLoader(paymentDetailsRef.current === null);
    setValidatedQuoteKey(null);
    setPaymentDetails(null);
    setQuoteError("");
    paymentValidate(
      merchantConfig.merchant_id,
      resolvedOrder.order_number,
      resolvedOrder.order_id,
      resolvedOrder.is_cod,
      resolvedOrder.total_order_price,
      return_type,
      shipmentMethod,
      updatedReturnedProducts,
      refundSource,
      resolvedOrder.preapplied_exchange_charge,
      resolvedOrder.preapplied_refund_charge,
      isWarrantyClaim,
    )
      .then((resp) => {
        if (obsolete || currentQuoteKeyRef.current !== quoteKey) return;
        setPaymentDetails(resp.data);
        setValidatedQuoteKey(quoteKey);
        setCalculatingLoader(false);
        setInitialLoader(false);
        setHitValidateApi("validate");
      })
      .catch((error) => {
        if (obsolete || currentQuoteKeyRef.current !== quoteKey) return;
        setPaymentDetails(null);
        setValidatedQuoteKey(null);
        setQuoteError(getApiErrorMessage(error, "Pricing could not be updated. Please retry before submitting."));
        setCalculatingLoader(false);
        setInitialLoader(false);
      });
    return () => { obsolete = true; };
  }, [
    quoteKey,
    quoteAttempt,
    hitValidateApi,
    isWarrantyClaim,
    isReadyForValidation,
    isRestoringState,
    merchantConfig.merchant_id,
    rdv,
    refundSource,
    requestCreated,
    resolvedOrder,
    return_type,
    shipmentMethod,
    updatedReturnedProducts,
  ]);

  useEffect(() => {
    setUpdatedReturnedProducts(returnedProducts);
  }, [returnedProducts]);

  const formatVariantSummary = (item) => {
    if (!item?.variant_name) {
      return "";
    }

    return item.variant_name
      .split("/")
      .map((part) => part.trim())
      .filter(Boolean)
      .join(" • ");
  };

  const renderCartItemPrice = (item) => {
    if (!item || item.product_price == null) {
      return null;
    }

    if (item.total_item_discount > 0) {
      return (
        <span className="line-item-price-stack">
          <span className="line-item-current-price">
            ₹ {item.discounted_price}
          </span>
          <span className="line-item-compare-price">
            ₹{item.original_product_price}
          </span>
        </span>
      );
    }

    return `₹ ${item.product_price}`;
  };

  const ReturnCartItemSummary = ({ product }) => {
    const hasCreateItemError = createItemErrorDetails.some((createError) =>
      createErrorMatchesProduct(product, createError),
    );
    const questionnaireAnswers = getWarrantyAnswers(product, paymentDetails, updatedReturnedProducts);
    const warrantyStatusLabel = isWarrantyClaim
      ? getWarrantyFlowLabel(product, paymentDetails, updatedReturnedProducts)
      : null;

    return (
      <LineItemCard
        product={product}
        imageSrc={product.variant_image_url}
        title={product.product_title}
        variant={formatVariantSummary(product)}
        quantity={product.quantity}
        price={renderCartItemPrice(product)}
        replacement={
          ["exchange", "replacement"].includes(product.return_type)
            ? {
              imageSrc: product.exchange_variant_image_url,
              title: product.exchange_product_title,
              variant: product.exchange_variant_name,
              quantity: product.quantity,
              price: product.exchange_product_price
                ? `₹ ${product.exchange_product_price}`
                : null,
            }
            : null
        }
        statusLabel={
          isWarrantyClaim
            ? warrantyStatusLabel
            : product.return_type === "replacement"
            ? "Replacement"
            : product.return_type === "exchange"
              ? "Exchange"
              : "Refund"
        }
        configured
        errorHighlighted={hasCreateItemError}
        primaryColor={merchantConfig.primary_theme_color}
        rounded={merchantConfig.use_rounded_edge_buttons}
        clickable={false}
      >
        {questionnaireAnswers.length > 0 && (
          <div className="line-item-questionnaire-review">
            {questionnaireAnswers.map((answer) => (
              <div
                className="line-item-questionnaire-row"
                key={`${product.line_item_id}-${answer.question_id ?? answer.question_id_snapshot}`}
              >
                <span>{answer.question_label}</span>
                <p>
                  {String(answer.answer_label ||
                    answer.answer_text ||
                    answer.answer_value)}
                </p>
                {answer.additional_comment && (
                  <p style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                    Additional details: {answer.additional_comment}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </LineItemCard>
    );
  };

  const WarrantyReview = () => {
    const primaryProduct = updatedReturnedProducts[0] || {};
    const warrantyAnswers = updatedReturnedProducts.flatMap((product) =>
      getWarrantyAnswers(product, paymentDetails, updatedReturnedProducts),
    );
    const repairComponents = updatedReturnedProducts.flatMap((product) =>
      flattenWarrantyComponents(product, paymentDetails, updatedReturnedProducts),
    );
    const feeRows = updatedReturnedProducts.flatMap((product) =>
      getWarrantyChargeSnapshots(getWarrantyLineItem(product, paymentDetails, updatedReturnedProducts))
        .filter((charge) => ["question", "service"].includes(charge.charge_category)),
    );
    const payableAmount = getWarrantyPaymentTotal(paymentDetails || {});
    const paidRepair = isWarrantyRepairFlow(
      updatedReturnedProducts,
      paymentDetails || {},
    );
    const issueLabel =
      primaryProduct.customer_reason_label ||
      warrantyAnswers[0]?.question_label ||
      "Warranty claim";
    const resolutionLabel = [...new Set(updatedReturnedProducts.map((product) =>
      getWarrantyFlowLabel(product, paymentDetails, updatedReturnedProducts),
    ))].join(" / ");
    const detailsTitle = `${resolutionLabel || "Warranty Claim"} Details`;
    const shippingCharge = Number(paymentDetails?.shipping_charges || 0);
    const workCharge = Math.max(0, payableAmount - shippingCharge);
    const componentRows = repairComponents;
    const showComponentReview = componentRows.length > 0;
    const nextSteps = payableAmount > 0
      ? [
          "Continue to payment",
          "Complete the payment",
          paidRepair
            ? "Pickup or repair instructions will be shared"
            : "Pickup or inspection instructions will be shared",
        ]
      : [
          "Submit your request",
          "The brand reviews your details",
          "Pickup or inspection instructions will be shared",
        ];

    return (
      <div className="warranty-review-flow">
        <div className="warranty-review-card warranty-details-card">
          <h2>{detailsTitle}</h2>
          <div className="warranty-review-row">
            <span>Issue</span>
            <strong>{issueLabel}</strong>
          </div>
          <div className="warranty-review-row">
            <span>Resolution</span>
            <strong>{resolutionLabel}</strong>
          </div>
          <div className="warranty-review-total-row">
            <span>Work charges</span>
            <strong>{formatCurrencyAmount(workCharge)}</strong>
          </div>
          {feeRows.map((charge, index) => (
            <div className="warranty-review-row" key={`fee-${index}`}>
              <span>Included {charge.charge_category === "service" ? "service fee" : "question fee"}{charge.label ? ` - ${charge.label}` : ""}</span>
              <strong>{formatCurrencyAmount(charge.amount ?? charge.charge_amount ?? 0)}</strong>
            </div>
          ))}
          <div className="warranty-review-row warranty-review-muted-row">
            <span>Pickup charge</span>
            <strong>{formatCurrencyAmount(shippingCharge)}</strong>
          </div>
          <div className="warranty-review-total-row">
            <span>Total payable</span>
            <strong>{formatCurrencyAmount(payableAmount)}</strong>
          </div>
          <div className="warranty-review-note">
            {paidRepair ? (
              <BuildOutlinedIcon aria-hidden="true" />
            ) : (
              <ShieldOutlinedIcon aria-hidden="true" />
            )}
            <p>
              {payableAmount > 0
                ? "Your request includes payable charges. Continue to payment to submit your request."
                : "No payment is required for this request. The brand will review your details after submission."}
            </p>
          </div>
        </div>

        {showComponentReview && (
          <div className="warranty-review-card warranty-components-card">
            <button
              type="button"
              className="warranty-card-toggle"
              aria-expanded={warrantyComponentsOpen}
              onClick={() => setWarrantyComponentsOpen((open) => !open)}
            >
              <span>
                <strong>What will be repaired?</strong>
                <small>
                  {repairComponents.length > 0
                    ? "Approximate component charges"
                    : "Review your answers"}
                </small>
              </span>
              <KeyboardArrowDownIcon
                aria-hidden="true"
                className={warrantyComponentsOpen ? "open" : ""}
              />
            </button>
            {warrantyComponentsOpen && (
              <div className="warranty-component-list">
                {componentRows.map((component, index) => (
                  <div
                    className={`warranty-component-row ${
                      component.image ? "" : "no-image"
                    }`}
                    key={`${component.key || component.name}-${index}`}
                  >
                    {component.image ? (
                      <img
                        src={component.image}
                        alt={component.name}
                        loading="lazy"
                      />
                    ) : null}
                    <span>{component.name}{component.covered ? " - Covered work" : component.waived ? " - Fee waived" : Number(component.amount) > 0 ? " - Paid work" : ""}</span>
                    <strong>{formatCurrencyAmount(component.amount)}</strong>
                  </div>
                ))}
                {repairComponents.length > 0 && (
                  <p className="warranty-component-footnote">
                    Final components will be confirmed after inspection.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        <div className="warranty-review-card warranty-next-card">
          <h2>What happens next?</h2>
          <div className="warranty-next-steps">
            {nextSteps.map((step, index) => (
              <div className="warranty-next-step" key={step}>
                <span>{index + 1}</span>
                <p>{step}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const getPaymentFailureMessage = (data) =>
    data?.detail ||
    data?.message ||
    data?.reason ||
    "Payment failed. Please try again.";

  const normalizePaymentResponse = (data = {}) => {
    const rawId = data.id ? String(data.id) : "";
    const looksLikeInvoiceId = rawId.startsWith("inv_");
    return {
      ...data,
      return_request_id:
        data.return_request_id ||
        data.request_id ||
        data.return_request?.id ||
        (!looksLikeInvoiceId ? rawId : ""),
      reference_id: data.reference_id || data.return_request?.reference_id || "",
      transaction_id: data.transaction_id || (looksLikeInvoiceId ? rawId : ""),
      order_id: data.order_id || resolvedOrder.order_id || "",
    };
  };

  const releaseCreateLock = () => {
    createSubmissionLockRef.current.release();
    setRequestCreated(false);
  };

  const recoverFromCreateConflict = async (error) => {
    const quoteConflict = getIykaQuoteConflict(error);
    if (quoteConflict) {
      releaseCreateLock();
      setPaymentDetails(quoteConflict.paymentDetails);
      dispatch(addPaymentDetails(quoteConflict.paymentDetails));
      toast.error(quoteConflict.message, {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 6000,
      });
      return true;
    }

    const conflict = getCreateConflict(error);
    if (!conflict) {
      return false;
    }

    releaseCreateLock();
    setCreateItemErrorDetails(
      conflict.additionalError ? [conflict.additionalError] : [],
    );

    try {
      const availability = await validateReturnCartAvailability(
        resolvedOrder,
        updatedReturnedProducts,
      );
      if (!hasActionableAvailabilityConflicts(availability)) {
        return false;
      }
      const refreshedUniqueId = availability.uniqueId || unique_id;
      dispatch(updateSelectedOrder(availability.freshOrder));
      dispatch(updateUUID(refreshedUniqueId));
      dispatch(addReturnedProducts(withAgentProducts(availability.availableItems)));
      setUpdatedReturnedProducts(availability.availableItems);
      saveReturnCartContext({
        ...(storedContext || {}),
        selectedOrder: availability.freshOrder,
        returnedProducts: withAgentProducts(availability.availableItems),
        uniqueId: refreshedUniqueId,
      });
    } catch (_) {
      return false;
    }

    toast.error(
      "This request conflicts with the latest item availability. Please review the updated order details.",
      {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
      },
    );
    let url = versionedPath("/products", location.pathname);
    if (encipherencode) {
      url += `?encipherencode=${encipherencode}`;
    }
    navigate(url);
    return true;
  };

  const handleCreateRequestError = (
    error,
    fallback = "Request cannot be created. Please try again",
  ) => {
    const message = getApiErrorMessage(error, fallback);
    const createErrors = normalizeCreateAdditionalErrors(error);

    releaseCreateLock();
    setCreateItemErrorDetails(createErrors);
    toast.error(message, {
      position: toast.POSITION.TOP_RIGHT,
      autoClose: 6000,
      hideProgressBar: false,
      closeOnClick: true,
    });

    if (createErrors.length > 0) {
      requestAnimationFrame(() => {
        document
          .querySelector(".line-item-card.error-highlighted")
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    }

    return message;
  };

  const submitGatewayForm = (redirectUrl, payuData = {}) => {
    const formElement = document.createElement("form");
    formElement.setAttribute("method", "post");
    formElement.setAttribute("action", redirectUrl);
    for (const [key, value] of Object.entries(payuData)) {
      const hiddenField = document.createElement("input");
      hiddenField.setAttribute("name", key);
      hiddenField.setAttribute("value", value);
      formElement.appendChild(hiddenField);
    }
    document.body.appendChild(formElement);
    formElement.submit();
  };

  const redirectToPaymentGateway = (rawPaymentData, requestUniqueId = unique_id) => {
    const paymentData = normalizePaymentResponse(rawPaymentData);
    if (agentDraft) {
      const operationId = resolveAgentDraftPaymentOperation(merchantConfig.merchant_id, resolvedOrder.order_id, paymentData) || requestUniqueId;
      if (!bindAgentDraftPayment(merchantConfig.merchant_id, resolvedOrder.order_id, operationId, paymentData)) {
        setPaymentDrawer((prev) => ({ ...prev, open: true, status: "failed", message: "Your agent draft is saved, but payment confirmation could not be retained. Please retry payment." }));
        releaseCreateLock();
        return false;
      }
    }
    setPaymentDrawer((prev) => ({
      ...prev,
      open: true,
      status: "redirecting",
      message: "Redirecting you to payment...",
      requestId: paymentData.return_request_id || prev.requestId,
      referenceId: paymentData.reference_id || prev.referenceId,
      transactionId: paymentData.transaction_id || prev.transactionId,
      orderId: paymentData.order_id || prev.orderId,
    }));

    if (paymentData.payu && paymentData.redirect_url) {
      submitGatewayForm(
        paymentData.redirect_url,
        paymentData.payu_data_dict || paymentData.payuDataDict,
      );
      return true;
    }

    if (paymentData.payment_url) {
      window.location.href = paymentData.payment_url;
      return true;
    }

    setPaymentDrawer((prev) => ({
      ...prev,
      open: true,
      status: "failed",
      message: "Payment link could not be generated. Please retry.",
    }));
    releaseCreateLock();
    return false;
  };

  const navigateToSuccess = (paymentData) => {
    let url = versionedPath("/success", location.pathname);
    const queryParams = new URLSearchParams();
    if (encipherencode) {
      queryParams.set("encipherencode", encipherencode);
    }
    if (paymentData.return_request_id || paymentData.request_id) {
      queryParams.set(
        "request_id",
        paymentData.return_request_id || paymentData.request_id,
      );
    }
    if (paymentData.reference_id) {
      queryParams.set("reference_id", paymentData.reference_id);
    }
    const queryString = queryParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
    navigate(url);
  };

  const retryPayment = () => {
    const requestId = paymentDrawer.requestId;
    if (!requestId && !paymentDrawer.referenceId) {
      setPaymentDrawer((prev) => ({
        ...prev,
        status: "failed",
        message: "Unable to retry payment. Please create the request again.",
      }));
      return;
    }

    setPaymentDrawer((prev) => ({
      ...prev,
      open: true,
      status: "generating",
      message: "Generating payment link...",
    }));
    generatePayment(
      requestId,
      paymentDrawer.transactionId,
      paymentDrawer.referenceId,
    )
      .then((resp) => {
        const paymentData = normalizePaymentResponse(resp.data);
        dispatch(addPaymentDetails(paymentData));
        redirectToPaymentGateway(paymentData);
      })
      .catch((err) => {
        setPaymentDrawer((prev) => ({
          ...prev,
          open: true,
          status: "failed",
          message: getApiErrorMessage(
            err,
            "Payment cannot be generated. Please try again.",
          ),
        }));
      });
  };

  const ensureAgentDraft = async (requestUniqueId, requestProducts, requestOrder) => {
    if (!agentDraft) return true;
    if (!mountedRef.current) return false;
    const shipping = requestOrder?.shipping_address || requestOrder?.billing_address || {};
    try {
      const receipt = await agentDraft.save({
        customer_first_name: firstNameCheck,
        customer_last_name: lastNameCheck,
        customer_email: userEmail,
        customer_phone_number: userPhoneNumber,
        customer_address_line_1: addressDetails?.addressLine1 || shipping.address1 || "",
        customer_address_line_2: addressDetails?.addressLine2 || shipping.address2 || "",
        customer_address_city: addressDetails?.city || shipping.city || "",
        customer_address_pin: addressDetails?.pincode || shipping.zip || "",
        customer_address_state: addressDetails?.state || shipping.province || "",
        customer_address_country: addressDetails?.country || shipping.country || "",
      }, requestConsentAccepted, requestUniqueId, requestProducts);
      if (!mountedRef.current) return false;
      if (!receipt) {
        releaseCreateLock();
        return false;
      }
      // Availability reconciliation changes remaining_qty, which does not affect the quoted selection.
      const comparableQuote = (value) => {
        const inputs = JSON.parse(value);
        inputs[7] = inputs[7].map(({ remaining_qty, ...item }) => item);
        return JSON.stringify(inputs);
      };
      if (comparableQuote(currentQuoteKeyRef.current) !== comparableQuote(quoteKey)) {
        releaseCreateLock();
        toast.error("Your selections changed. Please review the updated request before submitting.");
        return false;
      }
      return true;
    } catch {
      if (mountedRef.current) {
        releaseCreateLock();
        toast.error("The agent draft could not be confirmed. Please try again.");
      }
      return false;
    }
  };

  const successCreate = async (
    method = shipmentMethod,
    requestUniqueId = unique_id,
    availabilitySnapshot,
  ) => {
    const requestProducts =
      availabilitySnapshot?.availableItems || updatedReturnedProducts;
    const requestOrder = availabilitySnapshot?.freshOrder || resolvedOrder;
    if (agentDraft && !(await ensureAgentDraft(requestUniqueId, requestProducts, requestOrder))) return;
    setRequestCreated(true);
    createRequest(
      merchantConfig.merchant_id,
      encipherencode,
      [requestOrder],
      method,
      requestProducts,
      rmsConfig,
      addressDetails,
      upload_media,
      return_type,
      refundSource,
      refund_json && refund_json !== null ? refund_json : {},
      userEmail,
      userPhoneNumber,
      firstNameCheck,
      lastNameCheck,
      reference_id,
      uploadedMediaIds,
      paymentDetails.refund_charges + paymentDetails.preapplied_refund_charge,
      paymentDetails.exchange_charges +
      paymentDetails.preapplied_exchange_charge,
      paymentDetails.shipping_charges,
      requestUniqueId,
      refundSource === paymentDetails.offer_refund_mode
        ? paymentDetails.offer_refund_mode
        : null,
      refundSource === paymentDetails.offer_refund_mode
        ? paymentDetails.offer_refund_mode_on
        : null,
      refundSource === paymentDetails.offer_refund_mode
        ? paymentDetails.refund_mode_offer_amount
        : 0,
      rmsConfig.button_version,
      paymentDetails.discount_config,
      paymentDetails.total_exchange_charges,
      paymentDetails.total_refund_charges,
      paymentDetails.amount_to_pay,
      requestOrder.customer_id,
      paymentMethod,
      undefined,
      isWarrantyClaim,
      paymentDetails.warranty_charges ||
      paymentDetails.total_warranty_charges ||
      0,
      paymentDetails.pricing_breakdown || null,
    )
      .then((resp) => {
        const createdRequest = resp.data || {};
        const normalizedRequest = {
          ...createdRequest,
          completed_operation_id: requestUniqueId,
          return_request_id:
            createdRequest.return_request_id ||
            createdRequest.request_id ||
            createdRequest.id ||
            createdRequest.return_request?.id,
          reference_id:
            createdRequest.reference_id ||
            createdRequest.return_request?.reference_id,
        };
        dispatch(addPaymentDetails(normalizedRequest));
        let url = versionedPath("/success", location.pathname);
        const queryParams = new URLSearchParams();
        if (encipherencode) {
          queryParams.set("encipherencode", encipherencode);
        }
        if (normalizedRequest.return_request_id) {
          queryParams.set("request_id", normalizedRequest.return_request_id);
        }
        if (normalizedRequest.reference_id) {
          queryParams.set("reference_id", normalizedRequest.reference_id);
        }
        const queryString = queryParams.toString();
        if (queryString) url += `?${queryString}`;
        navigate(url);
      })
      .catch(async (err) => {
        if (await recoverFromCreateConflict(err)) {
          return;
        }
        handleCreateRequestError(err);
      });
  };

  const paymentMethodValidate = async (
    method = shipmentMethod,
    requestUniqueId = unique_id,
    availabilitySnapshot,
  ) => {
    const requestProducts =
      availabilitySnapshot?.availableItems || updatedReturnedProducts;
    const requestOrder = availabilitySnapshot?.freshOrder || resolvedOrder;
    const requiresCustomerPayment =
      Number(paymentDetails?.amount_to_pay || 0) > 0;
    const effectivePaymentMethod =
      isWarrantyClaim && requiresCustomerPayment && paymentMethod == null
        ? "prepaid"
        : paymentMethod;

    if (effectivePaymentMethod === null || effectivePaymentMethod === "cod") {
      setRequestCreated(true);
      successCreate(method, requestUniqueId, availabilitySnapshot);
    } else if (effectivePaymentMethod === "cod-prepaid") {
      releaseCreateLock();
      toast.error("Please select the mode of Payment", {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
      });
      if (window.screen.width < 800) {
        document
          .getElementById("paymentMode")
          .scrollIntoView({ behavior: "smooth" });
      }
      setPaymentMethodError(true);
    } else if (
      effectivePaymentMethod === "prepaid" &&
      !requiresCustomerPayment
    ) {
      setRequestCreated(true);
      successCreate(method, requestUniqueId, availabilitySnapshot);
    } else if (effectivePaymentMethod === "prepaid") {
      if (agentDraft && !(await ensureAgentDraft(requestUniqueId, requestProducts, requestOrder))) return;
      setPaymentDrawer({
        ...PAYMENT_DRAWER_INITIAL_STATE,
        open: true,
        status: "generating",
        message: "Generating payment link...",
      });
      createRequest(
        merchantConfig.merchant_id,
        encipherencode,
        [requestOrder],
        method,
        requestProducts,
        rmsConfig,
        addressDetails,
        upload_media,
        return_type,
        refundSource,
        refund_json && refund_json !== null ? refund_json : {},
        userEmail,
        userPhoneNumber,
        firstNameCheck,
        lastNameCheck,
        reference_id,
        uploadedMediaIds,
        paymentDetails.refund_charges + paymentDetails.preapplied_refund_charge,
        paymentDetails.exchange_charges +
        paymentDetails.preapplied_exchange_charge,
        paymentDetails.shipping_charges,
        requestUniqueId,
        refundSource === paymentDetails.offer_refund_mode
          ? paymentDetails.offer_refund_mode
          : null,
        refundSource === paymentDetails.offer_refund_mode
          ? paymentDetails.offer_refund_mode_on
          : null,
        refundSource === paymentDetails.offer_refund_mode
          ? paymentDetails.refund_mode_offer_amount
          : 0,
        rmsConfig.button_version,
        paymentDetails.discount_config,
        paymentDetails.total_exchange_charges,
        paymentDetails.total_refund_charges,
        paymentDetails.amount_to_pay,
        requestOrder.customer_id,
        effectivePaymentMethod,
        undefined,
        isWarrantyClaim,
        paymentDetails.warranty_charges ||
        paymentDetails.total_warranty_charges ||
        0,
        paymentDetails.pricing_breakdown || null,
      )
        .then((resp) => {
          const paymentData = normalizePaymentResponse(resp.data);
          dispatch(addPaymentDetails(paymentData));
          saveReturnCartContext({
            selectedOrder: requestOrder,
            returnedProducts: withAgentProducts(requestProducts),
            shipmentMethod: method,
            refundSource,
            refundJSON: refund_json,
            addressDetails,
            uploadMediaIds: uploadedMediaIds,
            uniqueId: requestUniqueId,
            customerContact: {
              firstNameCheck,
              lastNameCheck,
              userPhoneNumber,
              userEmail,
            },
          });
          redirectToPaymentGateway(paymentData, requestUniqueId);
          setPaymentMethod(null);
        })
        .catch(async (err) => {
          if (await recoverFromCreateConflict(err)) {
            return;
          }
          const message = handleCreateRequestError(err);
          setPaymentDrawer((prev) => ({
            ...prev,
            open: true,
            status: "failed",
            message,
          }));
        });
    }
  };

  const createReturnRequestAfterAvailabilityCheck = (
    method = shipmentMethod,
    requestUniqueId = unique_id,
    availabilitySnapshot,
  ) => {
    setCreateItemErrorDetails([]);
    setRefundModeError(false);

    if (
      addressChange ||
      wrongAddress ||
      userInfoError ||
      hasInvalidContact
    ) {
      releaseCreateLock();
      setAddressEditorOpen(true);
      setAddressFieldErrors(validateInlineAddressFields());
      return;
    }

    const showRefundError = (message = "Please select the mode of refund.") => {
      releaseCreateLock();
      toast.error(message, {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
      });
      setRefundModeError(true);
      if (window.screen.width < 800) {
        document
          .getElementById("Returnmode")
          ?.scrollIntoView({ behavior: "smooth" });
      }
    };

    if (method !== "") {
      const NEEDS_ACCOUNT_DETAILS = ["bank_transfer", "upi"];
      const mustValidate = rmsConfig.collect_refund_charges_on_request_creation;
      const selectedReasonFirstMethods = reasonFirstRefundItems.map(
        (item) => item.customer_preferred_refund_method,
      );
      const hasMissingReasonFirstMethod = hasReasonFirstRefundItems &&
        selectedReasonFirstMethods.some((selectedMethod) => !selectedMethod);
      const hasUnavailableReasonFirstMethod = reasonFirstRefundMethodUnavailableItems.length > 0;
      const requiresReasonFirstBankDetails = selectedReasonFirstMethods.includes("bank_transfer") ||
        (hasLegacyRefundItems && refundSource === "bank_transfer");
      const requiresReasonFirstUpiDetails = selectedReasonFirstMethods.includes("upi") ||
        (hasLegacyRefundItems && refundSource === "upi");
      const hasReasonFirstBankDetails = Boolean(
        refund_json?.bank_details?.bank_account_number &&
        refund_json?.bank_details?.confirm_bank_account_number &&
        refund_json?.bank_details?.bank_account_holder_name &&
        refund_json?.bank_details?.bank_ifsc_code,
      );
      const hasReasonFirstUpiDetails = Boolean(refund_json?.upi?.upi_code);
      const missingReasonFirstTransferDetails = hasReasonFirstRefundItems && (
        (requiresReasonFirstBankDetails && !hasReasonFirstBankDetails) ||
        (requiresReasonFirstUpiDetails && !hasReasonFirstUpiDetails)
      );
      const requiresDetails = hasReasonFirstRefundItems
        ? missingReasonFirstTransferDetails
        : NEEDS_ACCOUNT_DETAILS.includes(refundSource);
      const hasDetails = hasSavedRefundTransferDetails;
      const missingSource = (!hasReasonFirstRefundItems || hasLegacyRefundItems) && !refundSource;
      const missingDetails = hasReasonFirstRefundItems
        ? missingReasonFirstTransferDetails
        : requiresDetails && !hasDetails;

      const invalid = !isWarrantyClaim && (
        missingSource || missingDetails || hasMissingReasonFirstMethod || hasUnavailableReasonFirstMethod
      );

      if (return_type === "warranty_claim") {
        return paymentMethodValidate(method, requestUniqueId, availabilitySnapshot);
      }

      // ----- 1. Pure exchange/replacement (never touches refund flow) ------
      if (isExchangeOnlyRequestType(return_type)) {
        return paymentMethodValidate(
          method,
          requestUniqueId,
          availabilitySnapshot,
        );
      }

      // ----- 2. Any request containing refund items ------------------------
      if (hasRefundRequestType(return_type)) {
        if (invalid) {
          return showRefundError(
            hasUnavailableReasonFirstMethod
              ? "A refund method is unavailable for the calculated item refund. Go back to that item and choose another method."
              : hasMissingReasonFirstMethod
              ? "Please select a refund method for each refund item."
              : missingDetails
              ? "Please save refund details before creating the request."
              : undefined,
          );
        }
        if (return_type !== "refund") {
          return paymentMethodValidate(
            method,
            requestUniqueId,
            availabilitySnapshot,
          );
        }

        return mustValidate
          ? paymentMethodValidate(
            method,
            requestUniqueId,
            availabilitySnapshot,
          )
          : (
            setRequestCreated(true),
            successCreate(method, requestUniqueId, availabilitySnapshot)
          );
      }
      releaseCreateLock();
    } else {
      if (rmsConfig.show_shipping_preference) {
        releaseCreateLock();
        toast.error("Please select the shipping preference.", {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 6000,
          hideProgressBar: false,
          closeOnClick: true,
        });
      } else {
        const fallbackMethod = "merchant_handles_return_shipment";
        setShipmentMethod(fallbackMethod);
        releaseCreateLock();
      }
    }
  };

  const createReturnRequest = async (method = shipmentMethod) => {
    if (
      !hasCurrentQuote || calculatingLoader ||
      requestCreated ||
      !createSubmissionLockRef.current.tryAcquire()
    ) {
      return;
    }

    setRequestCreated(true);
    setCreateItemErrorDetails([]);

    try {
      const {
        freshOrder,
        uniqueId: validatedUniqueId,
        availableItems,
        conflicts,
      } = await validateReturnCartAvailability(
        resolvedOrder,
        updatedReturnedProducts,
      );
      const requestUniqueId = validatedUniqueId || unique_id;
      if (agentDraft && !mountedRef.current) return;

      if (currentQuoteKeyRef.current !== quoteKey) {
        releaseCreateLock();
        return;
      }

      dispatch(updateSelectedOrder(freshOrder));
      dispatch(updateUUID(requestUniqueId));
      dispatch(addReturnedProducts(withAgentProducts(availableItems)));
      setUpdatedReturnedProducts(availableItems);
      const reconciledContext = {
        selectedOrder: freshOrder,
        returnedProducts: withAgentProducts(availableItems),
        shipmentMethod,
        refundSource,
        refundJSON: refund_json,
        addressDetails,
        uploadMediaIds: uploadedMediaIds,
        uniqueId: requestUniqueId,
        customerContact: {
          firstNameCheck,
          lastNameCheck,
          userPhoneNumber,
          userEmail,
        },
      };
      setStoredContext(reconciledContext);
      saveReturnCartContext(reconciledContext);

      const quoteOrderFields = ["order_id", "order_number", "is_cod",
        "total_order_price", "preapplied_exchange_charge", "preapplied_refund_charge"];
      if (quoteOrderFields.some((field) => freshOrder[field] !== resolvedOrder[field])) {
        releaseCreateLock();
        return;
      }

      if (conflicts.length > 0) {
        setCreateItemErrorDetails(conflicts);
        releaseCreateLock();
        toast.error(
          "Your return selection changed because one or more items are no longer available. Please review the latest order details.",
          {
            position: toast.POSITION.TOP_RIGHT,
            autoClose: 6000,
            hideProgressBar: false,
            closeOnClick: true,
          },
        );
        let url = versionedPath("/products", location.pathname);
        if (encipherencode) {
          url += `?encipherencode=${encipherencode}`;
        }
        navigate(url);
        return;
      }

      createReturnRequestAfterAvailabilityCheck(
        method,
        requestUniqueId,
        {
          availableItems,
          freshOrder,
        },
      );
    } catch (error) {
      handleCreateRequestError(
        error,
        "We could not verify the latest item availability. Please try again.",
      );
    }
  };

  const isAddressIncomplete = addressChange || wrongAddress || userInfoError;
  const hasSavedRefundTransferDetails = hasReasonFirstRefundItems
    ? (
      (!reasonFirstRefundItems.some((item) => item.customer_preferred_refund_method === "bank_transfer") || Boolean(
        refund_json?.bank_details?.bank_account_number &&
        refund_json?.bank_details?.confirm_bank_account_number &&
        refund_json?.bank_details?.bank_account_holder_name &&
        refund_json?.bank_details?.bank_ifsc_code,
      )) &&
      (!reasonFirstRefundItems.some((item) => item.customer_preferred_refund_method === "upi") || Boolean(refund_json?.upi?.upi_code)) &&
      (!hasLegacyRefundItems || !["bank_transfer", "upi"].includes(refundSource) || (
        refundSource === "bank_transfer"
          ? Boolean(refund_json?.bank_details?.bank_account_number && refund_json?.bank_details?.confirm_bank_account_number && refund_json?.bank_details?.bank_account_holder_name && refund_json?.bank_details?.bank_ifsc_code)
          : Boolean(refund_json?.upi?.upi_code)
      ))
    )
    : refundSource === "bank_transfer"
      ? Boolean(
        refund_json?.refund_method === "bank_transfer" &&
        refund_json?.bank_details?.bank_account_number &&
        refund_json?.bank_details?.confirm_bank_account_number &&
        refund_json?.bank_details?.bank_account_holder_name &&
        refund_json?.bank_details?.bank_ifsc_code,
      )
      : refundSource === "upi"
        ? Boolean(
          refund_json?.refund_method === "upi" && refund_json?.upi?.upi_code,
        )
        : true;
  const isRefundTransferDetailsMissing =
    !isWarrantyClaim &&
    return_type !== "exchange" &&
    return_type !== "replacement" &&
    (hasReasonFirstRefundItems
      ? reasonFirstRefundItems.some((item) => ["bank_transfer", "upi"].includes(item.customer_preferred_refund_method)) ||
        (hasLegacyRefundItems && ["bank_transfer", "upi"].includes(refundSource))
      : ["bank_transfer", "upi"].includes(refundSource)) &&
    !hasSavedRefundTransferDetails;
  const isReasonFirstRefundMethodMissing = reasonFirstRefundItems.some(
    (item) => !item.customer_preferred_refund_method,
  );
  const isReasonFirstRefundMethodUnavailable = reasonFirstRefundMethodUnavailableItems.length > 0;
  const currentAddressErrors = isAddressIncomplete || hasInvalidContact
    ? validateInlineAddressFields()
    : {};
  const firstAddressError = Object.entries(currentAddressErrors)[0];
  const addressFieldLabels = {
    firstName: "First name",
    lastName: "Last name",
    email: "Email",
    phone: "Phone number",
    addressLine1: "Address Line 1",
    addressLine2: "Address Line 2",
    city: "City",
    state: "State",
    pincode: "PIN code",
  };
  const getAddressBlockerDetail = () => {
    if (!firstAddressError) {
      return "Please update pickup address details.";
    }

    const [fieldName, errorMessage] = firstAddressError;
    if (errorMessage === "*required") {
      return `${addressFieldLabels[fieldName] || "Detail"} is required`;
    }

    return errorMessage;
  };
  const paymentModeMissing =
    paymentDetails?.cod && paymentDetails?.prepaid && paymentMethod === "cod-prepaid";
  const shippingPreferenceMissing =
    rmsConfig.show_shipping_preference && !shipmentMethod;
  const createRequestBlocker = (() => {
    if (!(updatedReturnedProducts.length > 0)) {
      return {
        title: "No item selected",
        detail: "Add at least one item to create a request.",
        target: "items",
      };
    }
    if (requestCreated) {
      return {
        title: "Request is being created",
        detail: "Please wait while we finish this request.",
      };
    }
    if (initialLoader || calculatingLoader || !hasCurrentQuote) {
      return {
        title: "Details are loading",
        detail: "Please wait for latest pricing and options.",
      };
    }
    if (firstAddressError) {
      return {
        title: `${Object.keys(currentAddressErrors).length} detail missing`,
        detail: getAddressBlockerDetail(),
        target: "address",
      };
    }
    if (isReasonFirstRefundMethodUnavailable) {
      return {
        title: "Refund method unavailable",
        detail: "Go back to the affected item and choose a method supported by its calculated refund.",
        target: "refund",
      };
    }
    if (isReasonFirstRefundMethodMissing) {
      return {
        title: "Refund method missing",
        detail: "Choose a refund method for each refund item.",
        target: "refund",
      };
    }
    if (isAddressIncomplete) {
      return {
        title: "Address details missing",
        detail: "Please update pickup address details.",
        target: "address",
      };
    }
    if (isRefundTransferDetailsMissing) {
      return {
        title: "Refund details missing",
        detail: hasReasonFirstRefundItems
          ? [
              reasonFirstRefundItems.some((item) => item.customer_preferred_refund_method === "bank_transfer") && "bank account details",
              reasonFirstRefundItems.some((item) => item.customer_preferred_refund_method === "upi") && "UPI details",
            ].filter(Boolean).join(" and ") + " are required."
          : refundSource === "upi"
            ? "Please save UPI details."
            : "Please save bank account details.",
        target: "refund",
      };
    }
    if (paymentModeMissing) {
      return {
        title: "Payment mode missing",
        detail: "Please select Prepaid or COD.",
        target: "payment",
      };
    }
    if (shippingPreferenceMissing) {
      return {
        title: "Shipping preference missing",
        detail: "Please select a shipping preference.",
        target: "shipping",
      };
    }
    if (rmsConfig.request_consent && !requestConsentAccepted) {
      return {
        title: "Consent required",
        detail: "Please accept the request consent.",
        target: "consent",
      };
    }
    return null;
  })();
  const warrantyPayableAmount = getWarrantyPaymentTotal(paymentDetails || {});
  const warrantyFlowLabels = [...new Set(updatedReturnedProducts.map((product) =>
    getWarrantyFlowLabel(product, paymentDetails, updatedReturnedProducts),
  ))];
  const cartIntroTitle = agentDraft ? "Review your request" : isWarrantyClaim
    ? `${warrantyFlowLabels.length === 1 ? warrantyFlowLabels[0] : "Request"} Review`
    : "Select Resolution";
  const cartIntroDescription = agentDraft ? "Review all items and submit once. Payment applies only to items in the normal request." : isWarrantyClaim
    ? "Review the details below and submit your request."
    : "Choose how you would like to proceed with your item.";
  const createActionLabel = agentDraft ? "Submit request" : isWarrantyClaim
    ? warrantyPayableAmount > 0
      ? "Continue"
      : "Submit request"
    : "Create request";
  const handleCreateRequestBlocked = () => {
    if (!createRequestBlocker) {
      return;
    }

    if (createRequestBlocker.target === "consent") {
      setRequestConsentAttempted(true);
    }

    if (createRequestBlocker.target === "address") {
      setAddressEditorOpen(true);
      setAddressFieldErrors(validateInlineAddressFields());
      document
        .getElementById("returnPickupAddress")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    if (createRequestBlocker.target === "refund") {
      setRefundModeError(true);
      document
        .getElementById("Returnmode")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    if (createRequestBlocker.target === "payment") {
      setPaymentMethodError(true);
      document
        .getElementById("paymentMode")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    if (createRequestBlocker.target === "shipping") {
      const shippingTarget =
        document.querySelector(
          ".shipping-method-section, .shipment-method-section",
        ) || document.getElementById("returnPickupAddress");
      shippingTarget?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    if (createRequestBlocker.target === "consent") {
      document
        .querySelector(".request-consent-label")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  return (
    <StyledReturnCart
      sharpEdge={!merchantConfig.use_rounded_edge_buttons}
      rounded={merchantConfig.use_rounded_edge_buttons}
      primary_color={merchantConfig.primary_theme_color}
    >
      <PageHeader
        logoSrc={merchantConfig.brand_logo}
        logoAlt={merchantConfig.shop_display_name}
        primaryColor={merchantConfig.primary_theme_color}
        onBack={() => {
          if (agentDraft && (requestCreated || agentDraft.saving)) return;
          let url = versionedPath("/products", location.pathname);
          if (encipherencode) {
            url += `?encipherencode=${encipherencode}`;
          }
          if (isWarrantyClaim) {
            url += `${encipherencode ? "&" : "?"}claim_warrenty=true`;
          }
          navigate(url);
        }}
      />
      {requestCreated ? (
        <div
          key="request-creating-loader"
          className="request-creating-loader"
        >
          <div className="overlay" />
          <ClipLoader
            color="var(--rms-v2-text-body)"
            size={50}
            speedMultiplier={0.75}
            cssOverride={{ borderWidth: "6px" }}
          />
          <p className="request-creating-loader-text">
            Loading... <br /> Please do not refresh this page
          </p>
        </div>
      ) : (
        <StyledExchange
          key="return-cart-content"
          returnedProducts={updatedReturnedProducts}
          location={currentPath}
          refundModeError={refundModeError}
          primaryColor={merchantConfig.primary_theme_color}
          rounded={merchantConfig.use_rounded_edge_buttons}
        >
          <PageIntro
            title={cartIntroTitle}
            description={cartIntroDescription}
            className="v2-resolution-header"
            padding="10px 0"
          />
          {agentDraft && (
            <Stack spacing={2} sx={{ mb: 2 }}>
              <Alert severity="info">
                {agentDraft.receipt
                  ? `Agent draft saved: ${agentDraft.receipt.reference_id}. Complete your remaining request below.`
                  : "Submit once to send these items to an agent and complete your remaining request. No payment is collected for agent items."}
              </Alert>
              {agentDraft.error && <Alert severity="error">{agentDraft.error}</Alert>}
              {agentDraft.agentItems.map((item, index) => (
                <LineItemCard key={item.line_item_id || index} product={item} imageSrc={item.variant_image_url} title={item.product_title} variant={item.variant_name} quantity={item.quantity} statusLabel="Connect to Agent" clickable={false}>
                  <p>{item.customer_reason_label}{item.customer_sub_reason_label ? ` — ${item.customer_sub_reason_label}` : ""}</p>
                  {item.customer_reason_comment && <p>{item.customer_reason_comment}</p>}
                  {(item.questionnaire_answers || []).map((answer, answerIndex) => <p key={answerIndex}>{answer.question_label || answer.question_label_snapshot || "Answer"}: {String(answer.answer ?? answer.answer_text ?? answer.answer_label ?? answer.answer_value ?? "Recorded")}{answer.additional_comment ? ` — ${answer.additional_comment}` : ""}</p>)}
                  {(item.uploaded_media_ids || []).length > 0 && <p>{item.uploaded_media_ids.length} evidence file(s) attached</p>}
                </LineItemCard>
              ))}
            </Stack>
          )}
          <div className="products-return v2-flat-cart-list">
            <Grid
              alignItems="center"
              container
              gridTemplateColumns="repeat(12, 1fr)"
              className="cart-products"
            >
              {updatedReturnedProducts.length > 0 ? (
                updatedReturnedProducts.map((product, i) => {
                  return (
                    <Grid key={i} justifyContent="center" item xs={12}>
                      <ReturnCartItemSummary product={product} index={i} />
                    </Grid>
                  );
                })
              ) : (
                <Grid
                  item
                  xs={12}
                  className="empty-return-cart"
                >
                  <LazyLoadImage
                    effect="opacity"
                    src={cartImage}
                    alt=""
                    className="empty-return-cart-image"
                  />
                  <span className="empty-return-cart-text">
                    Ready to Return? Your Cart is Waiting!
                  </span>
                  <Button
                    variant="contained"
                    className="empty-return-cart-button"
                    onClick={() => {
                      let url = versionedPath("/products", location.pathname);
                      if (encipherencode) {
                        url += `?encipherencode=${encipherencode}`;
                      }
                      if (isWarrantyClaim) {
                        url += `${encipherencode ? "&" : "?"}claim_warrenty=true`;
                      }
                      navigate(url);
                    }}
                  >
                    Add Products
                  </Button>
                </Grid>
              )}
            </Grid>
          </div>

          {/* <div
              style={{
                borderRight: "2px dashed rgba(17, 17, 17, 0.2)",
                flex: "0.05",
              }}
            /> */}

          <div className="price-details">
            {!isWarrantyClaim && updatedReturnedProducts.length > 0 &&
              hasRefundRequestType(return_type) && (
                <PriceBox
                  refundSource={refundSource}
                  accountNumber={accountNumber}
                  confirmAccNumber={confirmAccNumber}
                  accHolderName={accHolderName}
                  ifsc={ifsc}
                  upiValue={upiValue}
                  returnedProducts={updatedReturnedProducts}
                  paymentDetails={hasCurrentQuote ? paymentDetails : null}
                  calculatingLoader={initialLoader || calculatingLoader}
                  userPhoneNumber={userPhoneNumber}
                  onReasonFirstRefundMethodChange={updateReasonFirstRefundMethod}
                />
              )}

            {isWarrantyClaim ? (
              initialLoader ? (
                <V2PaymentDetailsBoneyardLoader />
              ) : (
                <WarrantyReview />
              )
            ) : initialLoader ? (
              !hasRefundRequestType(return_type) && <V2PaymentDetailsBoneyardLoader />
            ) : (
              <div>
                {paymentDetails &&
                  paymentDetails !== null &&
                  return_type !== "refund" &&
                  !(
                    (rmsConfig.only_replacement || isReplacementRequest) &&
                    !hasExchangeFulfillmentChargeDetails(paymentDetails)
                  ) && (
                    <StyledPaymentDiv>
                      <p className="payment-title">
                        {isReplacementRequest
                          ? "Replacement"
                          : rmsConfig.only_replacement
                            ? "Replace"
                            : "Exchange"}{" "}
                        Details
                      </p>
                      <div className="subtotal-statement">
                        <p>
                          Subtotal of Old Products{" "}
                          {rmsConfig.only_replacement || isReplacementRequest
                            ? "(Replace)"
                            : "(Exchange)"}
                        </p>
                        <p
                          style={{
                            color: "var(--rms-v2-text-body)",
                            fontWeight: "400",
                            whiteSpace: "nowrap",
                            marginLeft: "1rem",
                          }}
                        >
                          ₹ {paymentDetails.old_item_total}
                        </p>
                      </div>

                      <div className="subtotal-statement">
                        <p>
                          Subtotal of New Products{" "}
                          {rmsConfig.only_replacement || isReplacementRequest
                            ? "(Replace)"
                            : "(Exchange)"}
                        </p>
                        <p
                          style={{
                            color: "var(--rms-v2-text-body)",
                            fontWeight: "400",
                            marginLeft: "1rem",
                            whiteSpace: "nowrap",
                          }}
                        >
                          ₹ {paymentDetails.new_item_total}
                        </p>
                      </div>

                      {paymentDetails.exchange_charges +
                        paymentDetails.preapplied_exchange_charge >
                        0 && (
                          <div className="subtotal-statement">
                            <p style={{ color: "var(--rms-v2-text-body)" }}>
                              {paymentDetails.exchange_charge_label &&
                                paymentDetails.exchange_charge_label !== ""
                                ? paymentDetails.exchange_charge_label
                                : "Handling Charges"}
                            </p>
                            <p
                              style={{
                                color: "var(--rms-v2-danger)",
                                fontWeight: "400",
                                marginLeft: "1rem",
                                whiteSpace: "nowrap",
                              }}
                            >
                              ₹{" "}
                              {paymentDetails.exchange_charges +
                                paymentDetails.preapplied_exchange_charge}
                            </p>
                          </div>
                        )}

                      {hasExchangeFulfillmentRequestType(return_type) &&
                        paymentDetails.shipping_charges > 0 && (
                          <div className="subtotal-statement">
                            <p style={{ color: "var(--rms-v2-text-body)" }}>
                              {paymentDetails.shipping_charge_label &&
                                paymentDetails.shipping_charge_label !== ""
                                ? paymentDetails.shipping_charge_label
                                : "Shipping Charges"}
                            </p>
                            <p
                              style={{
                                color: "var(--rms-v2-danger)",
                                fontWeight: "400",
                                marginLeft: "1rem",
                                whiteSpace: "nowrap",
                              }}
                            >
                              ₹ {paymentDetails.shipping_charges}
                            </p>
                          </div>
                        )}

                      {paymentDetails?.pricing_breakdown?.version === "v5" &&
                        Number(
                          paymentDetails?.pricing_breakdown
                            ?.waived_difference_total || 0,
                        ) > 0 && (
                          <div className="subtotal-statement">
                            <p style={{ color: "var(--rms-v2-success)" }}>
                              Exchange Price Waiver
                            </p>
                            <p
                              style={{
                                color: "var(--rms-v2-success)",
                                fontWeight: "400",
                                marginLeft: "1rem",
                                whiteSpace: "nowrap",
                              }}
                            >
                              - ₹{" "}
                              {formatPaymentAmount(
                                paymentDetails.pricing_breakdown
                                  .waived_difference_total,
                              )}
                            </p>
                          </div>
                        )}

                      {paymentDetails?.pricing_breakdown?.version === "v5" &&
                        Number(
                          paymentDetails?.pricing_breakdown
                            ?.additional_discount_total || 0,
                        ) > 0 && (
                          <div className="subtotal-statement">
                            <p style={{ color: "var(--rms-v2-success)" }}>
                              Additional Discount
                            </p>
                            <p
                              style={{
                                color: "var(--rms-v2-success)",
                                fontWeight: "400",
                                marginLeft: "1rem",
                                whiteSpace: "nowrap",
                              }}
                            >
                              - ₹{" "}
                              {formatPaymentAmount(
                                paymentDetails.pricing_breakdown
                                  .additional_discount_total,
                              )}
                            </p>
                          </div>
                        )}

                      {paymentDetails?.pricing_breakdown?.version === "v5" &&
                        Number(
                          paymentDetails?.pricing_breakdown
                            ?.threshold_waived_difference_total || 0,
                        ) > 0 && (
                          <div className="subtotal-statement">
                            <p style={{ color: "var(--rms-v2-success)" }}>
                              Threshold Waiver
                            </p>
                            <p
                              style={{
                                color: "var(--rms-v2-success)",
                                fontWeight: "400",
                                marginLeft: "1rem",
                                whiteSpace: "nowrap",
                              }}
                            >
                              - ₹{" "}
                              {formatPaymentAmount(
                                paymentDetails.pricing_breakdown
                                  .threshold_waived_difference_total,
                              )}
                            </p>
                          </div>
                        )}

                      {paymentDetails.discount_applied &&
                        paymentDetails?.pricing_breakdown?.version !== "v5" &&
                        paymentDetails?.discount_config?.target_type !==
                        "line_item" && (
                          <div className="subtotal-statement">
                            <p style={{ color: "var(--rms-v2-success)" }}>
                              Additional Discount ({" "}
                              {paymentDetails.discount_config.title} )
                            </p>
                            <p
                              style={{
                                color: "var(--rms-v2-success)",
                                fontWeight: "400",
                                marginLeft: "1rem",
                                whiteSpace: "nowrap",
                              }}
                            >
                              - ₹ {paymentDetails.total_discount_value}
                            </p>
                          </div>
                        )}

                      <div
                        style={{
                          borderTop: "1px dashed var(--rms-v2-text-muted)",
                          padding: "0.5rem 0px 0",
                          margin: "0.5rem 0 0",
                        }}
                        className="subtotal-statement"
                      >
                        {paymentDetails.amount_to_pay >= 0 ? (
                          <Fragment>
                            <p
                              style={{
                                color: merchantConfig.primary_theme_color,
                                fontWeight: "700",
                              }}
                            >
                              Total Payable Amount
                            </p>
                            <p
                              style={{
                                fontWeight: "700",
                                marginLeft: "1rem",
                                whiteSpace: "nowrap",
                                color: merchantConfig.primary_theme_color,
                              }}
                            >
                              {calculatingLoader
                                ? "Calculating..."
                                : `₹ ${paymentDetails.amount_to_pay}`}
                            </p>
                          </Fragment>
                        ) : paymentDetails.reimbursement > 0 ? (
                          <Fragment>
                            <p style={{ color: "var(--rms-v2-text-body)" }}>
                              You will get
                            </p>
                            <p
                              style={{
                                color: "var(--rms-v2-success)",
                                fontWeight: "700",
                                marginLeft: "1rem",
                                whiteSpace: "nowrap",
                              }}
                            >
                              ₹ {paymentDetails.reimbursement}
                            </p>
                          </Fragment>
                        ) : (
                          ""
                        )}
                      </div>
                      {(paymentDetails.reimbursement > 0 ||
                        paymentDetails.amount_to_pay < 0) && (
                          <p
                            style={{
                              color: "var(--rms-v2-text-body)",
                              fontSize: "12px",
                            }}
                            className="payment-reimbursement"
                          >
                            You will be reimbursed an amount of{" "}
                            <span
                              style={{
                                color: "var(--rms-v2-success)",
                                fontWeight: "600",
                              }}
                            >
                              ₹ {Number(paymentDetails.reimbursement)}
                            </span>
                            * (Final refund amount may vary and will be confirmed at the time of reimbursement)
                          </p>
                        )}
                    </StyledPaymentDiv>
                  )}
                {paymentDetails !== null && (
                  <ResolutionNotes
                    margin="0"
                    rounded={merchantConfig.use_rounded_edge_buttons}
                    notes={[
                      hasRefundRequestType(return_type) && {
                        title: "Refund Note:",
                        content: rmsConfig.brand_refund_note,
                      },
                      hasExchangeFulfillmentRequestType(return_type) && {
                        title: `${
                          isReplacementRequest || rmsConfig.only_replacement
                            ? "Replacement"
                            : "Exchange"
                        } Note:`,
                        content: rmsConfig.brand_exchange_note,
                      },
                    ].filter(Boolean)}
                  />
                )}
              </div>
            )}
            {paymentDetails &&
              paymentDetails !== null &&
              paymentDetails.cod &&
              paymentDetails.prepaid && (
                <div
                  className={`payment-method-card ${paymentMethodError ? "payment-method-error" : ""
                    }`}
                >
                  <p
                    id="paymentMode"
                    className="payment-method-title"
                  >
                    Select payment mode
                  </p>
                  <div
                    aria-labelledby="paymentMode"
                    className="payment-method-options"
                    role="radiogroup"
                  >
                    <button
                      aria-checked={paymentMethod === "prepaid"}
                      className={`payment-method-option ${paymentMethod === "prepaid" ? "selected" : ""
                        }`}
                      onClick={() => {
                        setPaymentMethod("prepaid");
                        setPaymentMethodError(false);
                      }}
                      role="radio"
                      type="button"
                      value="prepaid"
                    >
                      <CheckmarkControl
                        className="payment-method-check"
                        primaryColor={merchantConfig.primary_theme_color}
                        selected={paymentMethod === "prepaid"}
                        size={18}
                      />
                      <span>Prepaid</span>
                    </button>
                    <button
                      aria-checked={paymentMethod === "cod"}
                      className={`payment-method-option ${paymentMethod === "cod" ? "selected" : ""
                        }`}
                      onClick={() => {
                        setPaymentMethod("cod");
                        setPaymentMethodError(false);
                      }}
                      role="radio"
                      type="button"
                      value="cod"
                    >
                      <CheckmarkControl
                        className="payment-method-check"
                        primaryColor={merchantConfig.primary_theme_color}
                        selected={paymentMethod === "cod"}
                        size={18}
                      />
                      <span>COD</span>
                    </button>
                  </div>
                </div>
              )}
            <div
              className="pickup-address-section v2-address-section"
              id="returnPickupAddress"
            >
              {addressChange && (
                <div className="v2-address-inline-error">
                  Please update your customer/address details below
                </div>
              )}
              <div className="v2-address-header-row">
                <p>Return Pickup</p>
                {!addressEditorOpen && (
                  <button
                    type="button"
                    className="v2-address-edit-btn"
                    onClick={() => setAddressEditorOpen(true)}
                    aria-label="Edit pickup address"
                  >
                    <EditIcon />
                  </button>
                )}
              </div>
              {!addressEditorOpen && (
                <PickupAddress
                  setAddressChange={setAddressChange}
                  edit={false}
	                  updatedAddress={addressDetails}
	                  serviceable={
	                    shipmentMethod === "customer_ships_back" ? false : true
	                  }
                  shipmentMethod={shipmentMethod}
                  setShipmentMethod={syncShipmentMethod}
                  disabled={false}
                />
              )}
              {addressEditorOpen && (
                <div className="v2-address-editor">
                  <div className="v2-address-editor-grid">
                    <TextField
                      label="First Name"
                      value={firstNameCheck || ""}
                      onChange={(e) => {
                        setFirstNameCheck(e.target.value);
                        clearAddressFieldError("firstName");
                      }}
                      error={Boolean(addressFieldErrors.firstName)}
                      helperText={addressFieldErrors.firstName || ""}
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label="Last Name"
                      value={lastNameCheck || ""}
                      onChange={(e) => {
                        setLastNameCheck(e.target.value);
                        clearAddressFieldError("lastName");
                      }}
                      error={Boolean(addressFieldErrors.lastName)}
                      helperText={addressFieldErrors.lastName || ""}
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label="Email"
                      value={userEmail || ""}
                      onChange={(e) => {
                        setUserEmail(e.target.value);
                        clearAddressFieldError("email");
                      }}
                      error={Boolean(
                        addressFieldErrors.email || emailValidationError,
                      )}
                      helperText={
                        addressFieldErrors.email || emailValidationError
                      }
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label="Phone Number"
                      value={userPhoneNumber || ""}
                      onChange={(e) => {
                        setUserPhoneNumber(e.target.value);
                        clearAddressFieldError("phone");
                      }}
                      error={Boolean(
                        addressFieldErrors.phone || phoneValidationError,
                      )}
                      helperText={
                        addressFieldErrors.phone || phoneValidationError
                      }
                      inputProps={{
                        type: "tel",
                        inputMode: "tel",
                        maxLength: 13,
                      }}
                      fullWidth
                      size="small"

                    />
                    <TextField
                      label="Address Line 1"
                      value={editAddressLine1}
                      onChange={(e) => {
                        setEditAddressLine1(e.target.value);
                        clearAddressFieldError("addressLine1");
                      }}
                      error={Boolean(addressFieldErrors.addressLine1)}
                      helperText={addressFieldErrors.addressLine1 || ""}
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label="Address Line 2"
                      value={editAddressLine2}
                      onChange={(e) => {
                        setEditAddressLine2(e.target.value);
                        clearAddressFieldError("addressLine2");
                      }}
                      error={Boolean(addressFieldErrors.addressLine2)}
                      helperText={addressFieldErrors.addressLine2 || ""}
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label="City"
                      value={editCity}
                      onChange={(e) => {
                        setEditCity(e.target.value);
                        clearAddressFieldError("city");
                      }}
                      error={Boolean(addressFieldErrors.city)}
                      helperText={addressFieldErrors.city || ""}
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label="State"
                      value={editState}
                      onChange={(e) => {
                        setEditState(e.target.value);
                        clearAddressFieldError("state");
                      }}
                      error={Boolean(addressFieldErrors.state)}
                      helperText={addressFieldErrors.state || ""}
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label="PIN Code"
                      value={editPincode}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, "");
                        setEditPincode(digits.slice(0, 6));
                        clearAddressFieldError("pincode");
                      }}
                      error={Boolean(addressFieldErrors.pincode)}
                      helperText={addressFieldErrors.pincode || ""}
                      fullWidth
                      size="small"
                    />
                  </div>
                  <div className="v2-address-editor-actions">
                    <Button
                      variant="outlined"
                      className="v2-address-cancel-button"
                      onClick={() => {
                        setAddressEditorOpen(false);
                        setAddressEditorError("");
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      className="v2-address-save-button"
                      disabled={hasInvalidContact}
                      onClick={handleSaveInlineAddress}
                    >
                      Save Address
                    </Button>
                  </div>
                </div>
              )}
              <hr style={{ margin: "1rem 0px" }} />
            </div>
            {quoteError && (
              <div role="alert" className="v2-address-inline-error">
                {quoteError}
                <Button onClick={() => setQuoteAttempt((attempt) => attempt + 1)}>Retry pricing</Button>
              </div>
            )}
            <BottomActionBar
              actionLabel={createActionLabel}
              count={updatedReturnedProducts.length + (agentDraft?.pendingAgentItems.length || 0)}
              rounded={merchantConfig.use_rounded_edge_buttons}
              topContent={
                rmsConfig.request_consent &&
                  rmsConfig.request_consent_text !== "" ? (
                  <label
                    className={`request-consent-label ${createRequestBlocker?.target === "consent" &&
                        requestConsentAttempted
                        ? "request-consent-card error"
                        : ""
                      }`}
                  >
                    <input
                      type="checkbox"
                      checked={requestConsentAccepted}
                      onChange={(e) => {
                        setRequestConsentAccepted(e.target.checked);
                        if (e.target.checked) {
                          setRequestConsentAttempted(false);
                        }
                      }}
                    />
                    <CheckmarkControl
                      className="request-consent-check"
                      fontSize={11}
                      primaryColor={merchantConfig.primary_theme_color}
                      selected={requestConsentAccepted}
                      size={16}
                    />
                    <span className="request-consent-copy">
                      {createRequestBlocker?.target === "consent" &&
                        requestConsentAttempted ? (
                        <strong>Consent required</strong>
                      ) : null}
                      <span className="request-consent-text">
                        {rmsConfig.request_consent_text}
                      </span>
                    </span>
                  </label>
                ) : null
              }
              disabled={Boolean(createRequestBlocker)}
              onDisabledAction={handleCreateRequestBlocked}
              onAction={() => {
                createReturnRequest();
              }}
            />

            {/* {merchantConfig.whatsapp_help &&
                merchantConfig.whatsapp_help !== null && (
                  <a
                    href={merchantConfig.whatsapp_help}
                    target="_blank"
                    rel="noreferrer"
                    className="help"
                    style={{ display: "block" }}
                  >
                    Need Help?
                  </a>
                )} */}
          </div>
        </StyledExchange>
      )}
      <Drawer
        anchor="bottom"
        open={paymentDrawer.open}
        onClose={() => {
          if (paymentDrawer.status === "failed" && !paymentDrawer.paymentReceived) {
            setPaymentDrawer(PAYMENT_DRAWER_INITIAL_STATE);
          }
        }}
        PaperProps={{
          style: {
            width: "min(100vw, 500px)",
            maxWidth: "100vw",
            left: "max(0px, calc((100vw - 500px) / 2))",
            right: "auto",
            borderTopLeftRadius: merchantConfig.use_rounded_edge_buttons
              ? "18px"
              : "0px",
            borderTopRightRadius: merchantConfig.use_rounded_edge_buttons
              ? "18px"
              : "0px",
            padding: "22px 20px 24px",
            boxSizing: "border-box",
            background: "var(--rms-v2-section-bg)",
            overflow: "visible",
          },
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: "48px",
              height: "48px",
              borderRadius: "50%",
              margin: "0 auto 14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background:
                paymentDrawer.status === "failed"
                  ? "var(--rms-v2-danger-soft)"
                  : paymentDrawer.status === "success"
                    ? "var(--rms-v2-success-soft)"
                    : "var(--rms-v2-primary-soft)",
              color:
                paymentDrawer.status === "failed"
                  ? "var(--rms-v2-danger)"
                  : paymentDrawer.status === "success"
                    ? "var(--rms-v2-success)"
                    : merchantConfig.primary_theme_color,
              fontSize: "24px",
              fontWeight: 900,
            }}
          >
            {paymentDrawer.status === "failed" ? (
              "!"
            ) : paymentDrawer.status === "success" ? (
              "✓"
            ) : (
              <ClipLoader
                color={merchantConfig.primary_theme_color}
                size={24}
                speedMultiplier={0.75}
              />
            )}
          </div>
          <h2
            style={{
              margin: "0 0 8px",
              color: "var(--rms-v2-text-title)",
              fontSize: "18px",
              fontWeight: 900,
            }}
          >
            {paymentDrawer.status === "failed"
              ? paymentDrawer.paymentReceived ? "Payment Received — Request Needs Assistance" : "Payment Failed"
              : paymentDrawer.status === "success"
                ? "Payment Successful"
                : "Generating Payment"}
          </h2>
          <p
            style={{
              margin: "0 auto 16px",
              maxWidth: "330px",
              color: "var(--rms-v2-text-muted)",
              fontSize: "13px",
              lineHeight: 1.5,
            }}
          >
            {paymentDrawer.message ||
              "Please wait while we prepare your payment."}
          </p>
          {(paymentDrawer.transactionId || paymentDrawer.referenceId) && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: "14px",
                padding: "14px 0 0",
                borderTop: "1px solid var(--rms-v2-border-soft)",
                textAlign: "left",
              }}
            >
              {paymentDrawer.transactionId && (
                <div>
                  <p
                    style={{
                      margin: "0 0 8px",
                      color: "var(--rms-v2-text-muted)",
                      fontSize: "11px",
                      fontWeight: 900,
                      textTransform: "uppercase",
                    }}
                  >
                    Transaction ID
                  </p>
                  <p
                    style={{
                      margin: 0,
                      color: "var(--rms-v2-text-title)",
                      fontSize: "13px",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {paymentDrawer.transactionId}
                  </p>
                </div>
              )}
              {paymentDrawer.referenceId && (
                <div>
                  <p
                    style={{
                      margin: "0 0 8px",
                      color: "var(--rms-v2-text-muted)",
                      fontSize: "11px",
                      fontWeight: 900,
                      textTransform: "uppercase",
                    }}
                  >
                    Reference ID
                  </p>
                  <p
                    style={{
                      margin: 0,
                      color: "var(--rms-v2-text-title)",
                      fontSize: "13px",
                      overflowWrap: "anywhere",
                    }}
                  >
                    #{paymentDrawer.referenceId}
                  </p>
                </div>
              )}
            </div>
          )}
          {paymentDrawer.status === "failed" && !paymentDrawer.paymentReceived && (
            <Button
              variant="contained"
              onClick={retryPayment}
              style={{
                width: "100%",
                height: "46px",
                marginTop: "18px",
                borderRadius: merchantConfig.use_rounded_edge_buttons
                  ? "10px"
                  : "0px",
                background: merchantConfig.primary_theme_color,
                color: "var(--rms-v2-surface)",
                fontWeight: 900,
                textTransform: "none",
              }}
            >
              Retry Payment
            </Button>
          )}
        </div>
      </Drawer>
    </StyledReturnCart>
  );
}

export default function ReturnCartWithAgentDrafts() {
  return (
    <AgentDraftGate renderMixedCheckout={(controller) => <ReturnCart agentDraft={controller} />}>
      <ReturnCart />
    </AgentDraftGate>
  );
}
